import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Polygon, Marker, Tooltip, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Predictions.css';
import { parseServerTime } from '../utils/serverTime';
import { errorToBandClass } from '../utils/errorBands';
import { getAccessToken } from '../utils/auth';
import {
  getPredictionsForDate,
  getPredictionHistory,
  getPredictionAccuracyHeatmap,
  clearPredictionAccuracyHeatmap
} from '../utils/httpApi';
import { arrivalTrackQuery } from '../utils/landingTrack';
import useLandingTrack from '../hooks/useLandingTrack';
import { LandingTrackLine, LandingTrackPopup } from './LandingTrack';
import { getTileLayerConfig, getMapFilter, MAP_STYLES, UNIT_SYSTEMS, convertDistance, convertAltitude } from '../utils/mapStyles';
import { useSettings } from '../contexts/SettingsContext';
import { FlagIcon, AirlineLogo, AircraftTypeIcon } from './AircraftTableComponents';
import { formatEtaTime, formatServerTime } from '../utils/timezones';
import { resolveCountryCode } from '../utils/aircraftCountry';
import AircraftMarker from './AircraftMarker';
import PredictionQualityChart from './PredictionQualityChart';
import PredictionDistributionBar, { parseDistribution } from './PredictionDistributionBar';
import { arrivalOutlook, ArrivalOutlookBar, LateTrendSparkline } from './ArrivalOutlook';
import PredictionsDepartures from './PredictionsDepartures';
import PredictionsOnGround from './PredictionsOnGround';
import PredictionAccuracy from './PredictionAccuracy';
import { HoldCellContent, holdRank, mergeLiveHold } from './HoldIndicator';
import ApproachFlags from './ApproachFlags';
import ArrivalFlowBadge from './ArrivalFlowBadge';
import { getEmergencySquawk } from '../utils/emergencySquawk';

// Supported airports for predictions (shared with the mobile Airport board)
import { SUPPORTED_AIRPORTS } from '../utils/supportedAirports';

// ICAO → IATA airport-code map for converting backend dest_airport (ICAO)
// to the 3-letter IATA code shown in the popup. Limited to the airports
// we either serve as destinations or commonly see as origins/destinations
// in the supported regions. Falls back to the ICAO when not mapped.
/**
 * Minutes without a prediction after which we treat a flight as out of contact.
 *
 * One constant, because three different thresholds were the whole problem: the
 * map marker disappeared at 10 minutes, the table kept counting down off a dead
 * ETA indefinitely, and the status flipped to "Landed (est)" on an unrelated
 * rule. A flight we lost is now the same flight on the map, in the countdown and
 * in the status column.
 */
const STALE_CONTACT_MIN = 10;

// Backend summary refresh while today is shown: no sooner than MIN after the last read
// (even when a new flight has just appeared), and no later than MAX regardless. The
// summary runs seven queries over the day's prediction_event rows, so not every tick.
const SUMMARY_REFRESH_MIN_MS = 60 * 1000;
const SUMMARY_REFRESH_MAX_MS = 5 * 60 * 1000;

const ICAO_TO_IATA = {
  EIDW: 'DUB', EICK: 'ORK', EINN: 'SNN', EIKN: 'NOC', EIKY: 'KIR',
  LPPR: 'OPO', LPPT: 'LIS', LPMA: 'FNC', LPFR: 'FAO', LPAZ: 'SMA',
  EGLL: 'LHR', EGKK: 'LGW', EGSS: 'STN', EGLC: 'LCY', EGGW: 'LTN',
  EGCC: 'MAN', EGPF: 'GLA', EGPH: 'EDI', EGAA: 'BFS', EGNX: 'EMA',
  EGNT: 'NCL', EGGD: 'BRS', EGGP: 'LPL', EGBB: 'BHX',
  LFPG: 'CDG', LFPO: 'ORY', LFML: 'MRS', LFLL: 'LYS', LFBO: 'TLS',
  EHAM: 'AMS', EBBR: 'BRU', ELLX: 'LUX',
  EDDF: 'FRA', EDDM: 'MUC', EDDB: 'BER', EDDH: 'HAM', EDDK: 'CGN',
  LSZH: 'ZRH', LOWW: 'VIE',
  LEMD: 'MAD', LEBL: 'BCN', LEMG: 'AGP', LEPA: 'PMI', LEAL: 'ALC',
  LEIB: 'IBZ', LEMH: 'MAH', LEZL: 'SVQ', LEVC: 'VLC',
  LIRF: 'FCO', LIMC: 'MXP', LIPZ: 'VCE', LIRA: 'CIA',
  LGAV: 'ATH', LGTS: 'SKG', LGRP: 'RHO', LGIR: 'HER',
  LKPR: 'PRG', LZIB: 'BTS', LHBP: 'BUD', LROP: 'OTP',
  LTFM: 'IST', LTBA: 'ISL',
  EKCH: 'CPH', ESSA: 'ARN', ENGM: 'OSL', EFHK: 'HEL',
  EPWA: 'WAW', EPKK: 'KRK', EPGD: 'GDN',
  KJFK: 'JFK', KEWR: 'EWR', KORD: 'ORD', KATL: 'ATL', KLAX: 'LAX',
  CYYZ: 'YYZ', CYUL: 'YUL', CYVR: 'YVR',
  OMDB: 'DXB', OTHH: 'DOH', OEJN: 'JED', OERK: 'RUH',
  GMMN: 'CMN', GCLP: 'LPA', GCTS: 'TFS', GCXO: 'TFN',
};
function icaoToIata(icao) {
  if (!icao) return null;
  return ICAO_TO_IATA[icao.trim().toUpperCase()] || null;
}

function airportTzName(icao) {
  return SUPPORTED_AIRPORTS.find(a => a.icao === icao)?.tz || 'UTC';
}

// Resolve a CSS variable at runtime — Leaflet's pathOptions need a literal
// colour string, so we read the live computed value off the root element.
function cssVar(name) {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// Common airline ICAO → human name. Extend as needed; falls back to the ICAO
// code in the dropdown when not found.
const AIRLINE_NAMES = {
  EIN: 'Aer Lingus',         RYR: 'Ryanair',           BAW: 'British Airways',
  EZY: 'easyJet',            EZS: 'easyJet Switzerland', AAL: 'American',
  DAL: 'Delta',              UAL: 'United',            ACA: 'Air Canada',
  AFR: 'Air France',         KLM: 'KLM',               DLH: 'Lufthansa',
  SWR: 'Swiss',              AUA: 'Austrian',          IBE: 'Iberia',
  IBK: 'Iberia Express',     VLG: 'Vueling',           TAP: 'TAP Portugal',
  TAY: 'TAP Air Portugal',   TVF: 'Transavia France',  TRA: 'Transavia',
  AEE: 'Aegean',             KLC: 'KLM Cityhopper',    LOT: 'LOT Polish',
  TAR: 'Tunisair',           THY: 'Turkish',           QTR: 'Qatar',
  UAE: 'Emirates',           ETD: 'Etihad',            DHL: 'DHL',
  FDX: 'FedEx',              UPS: 'UPS',               BCS: 'European Air Transport',
  CFE: 'BA CityFlyer',       LGL: 'Luxair',            FIN: 'Finnair',
  SAS: 'SAS',                NAX: 'Norwegian',         WUK: 'Wizz Air UK',
  WZZ: 'Wizz Air',           VOZ: 'Virgin Australia',  VIR: 'Virgin Atlantic',
  ICE: 'Icelandair',         FFT: 'Frontier',          GES: 'Gestair',
  EAI: 'Air Corps',          MAS: 'Malaysia',          ROT: 'Tarom',
};
function airlineLabel(icao) {
  if (!icao) return '—';
  const name = AIRLINE_NAMES[icao];
  return name ? `${icao} — ${name}` : icao;
}

// ICAO Wake Turbulence Cat H — kept in sync with backend
// DepartureSessionTracker.HEAVY_AIRCRAFT_TYPES. Flagged with a small "H"
// badge on inbound + outbound rows so operators can spot wake-vortex
// separation candidates at a glance.
const HEAVY_AIRCRAFT_TYPES = new Set([
  'A332','A333','A337','A338','A339','A359','A35K','A388',
  'B741','B742','B743','B744','B748','B74F','B74S','B752','B753',
  'B762','B763','B764','B772','B77F','B77L','B77W','B788','B789','B78X',
  'DC10','IL62','IL76','IL86','IL96','MD11','TU16','A124','A225',
  'C5','C17','C141','KC10','KC30','KC46','E767'
]);
function isHeavyType(aircraftType) {
  if (!aircraftType) return false;
  return HEAVY_AIRCRAFT_TYPES.has(aircraftType.trim().toUpperCase());
}

// Format minutes as "Xh Ym" (still used for long-range ETAs in tooltips)
function formatMinutes(min) {
  if (min == null) return '-';
  const sign = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  if (abs < 60) return `${sign}${abs.toFixed(1)}m`;
  const h = Math.floor(abs / 60);
  const m = Math.round(abs - h * 60);
  return `${sign}${h}h ${m}m`;
}

// Format a remaining-minutes value as "Xm Ys" (or "Xh Ym Zs" if hours).
// Used for the headline "Remaining" column. No "m" suffix on the bare number.
function formatRemainingMmSs(min) {
  if (min == null || isNaN(min)) return '-';
  const sign = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  const totalSec = Math.round(abs * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${sign}${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  return `${sign}${m}m ${String(s).padStart(2, '0')}s`;
}

// HH:MM in airport's local time, with NO timezone suffix
// (used for ETA Time + Status columns where TZ is implicit from header).
function formatLocalHourMin(date, airportIcao) {
  if (!date) return '-';
  try {
    const d = (date instanceof Date) ? date : parseServerTime(date);
    if (!d) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: airportTzName(airportIcao)
    }).format(d);
  } catch {
    return '-';
  }
}

// Format a delta with sign and color hint
function formatDelta(min) {
  if (min == null) return '-';
  const sign = min > 0 ? '+' : '';
  return `${sign}${min.toFixed(1)}m`;
}

// Color scale for ETA error — slightly darker shades for readability on light bg.
function errorToColor(errorMin) {
  const e = Math.abs(errorMin);
  if (e <= 1) return '#15803d';   // dark green
  if (e <= 2) return '#a16207';   // dark amber
  if (e <= 5) return '#c2410c';   // dark orange
  return '#b91c1c';               // dark red
}

// Two-line sortable header: line 1 sorts by k1, line 2 by k2 (when given).
function SortTh({ sort, k1, l1, t1, k2, l2, t2, numeric, className }) {
  const arrow = (k) => (sort.config.key === k ? (sort.config.direction === 'asc' ? ' ▲' : ' ▼') : '');
  const cls = ['pt-th', numeric ? 'numeric' : '', className || ''].filter(Boolean).join(' ');
  return (
    <th className={cls}>
      <div className="pt-l1" title={t1} onClick={() => sort.onSort(k1)}>{l1}{arrow(k1)}</div>
      {l2 && (
        <div className={`pt-l2${k2 ? '' : ' pt-th-static'}`} title={t2 || t1}
          onClick={k2 ? (e) => { e.stopPropagation(); sort.onSort(k2); } : undefined}>
          {l2}{k2 ? arrow(k2) : ''}
        </div>
      )}
    </th>
  );
}

// Distance-band aware MAE → colour mapping. Each band has its own
// green/orange/red thresholds because what counts as a "good" prediction
// is much tighter close to the airport than out at long range:
//   ≤ 10 km        → green < 1m, orange 1–3m, red > 3m
//   10–100 km      → green < 3m, orange 3–5m, red > 5m
//   > 100 km       → green ≤ 5m, orange 5–10m, red > 10m
// Distance band is determined by the sector's start distance.
function maeToColor(mae, distanceStartKm) {
  if (mae == null) return '#6b7280';  // grey for no data
  const GREEN  = '#15803d';
  const ORANGE = '#c2410c';
  const RED    = '#b91c1c';
  const dist = distanceStartKm == null ? 0 : distanceStartKm;
  if (dist >= 100) {
    if (mae <= 5)  return GREEN;
    if (mae <= 10) return ORANGE;
    return RED;
  }
  if (dist >= 10) {
    if (mae < 3) return GREEN;
    if (mae <= 5) return ORANGE;
    return RED;
  }
  // ≤ 10 km
  if (mae < 1) return GREEN;
  if (mae <= 3) return ORANGE;
  return RED;
}

// Calculate point at bearing/distance from origin (Haversine forward)
function calculatePoint(lat, lon, bearingDeg, distanceKm) {
  const R = 6371;
  const bearing = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const d = distanceKm / R;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing));
  const lon2 = lon1 + Math.atan2(
    Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
  );
  return [(lat2 * 180) / Math.PI, (lon2 * 180) / Math.PI];
}

// Auto-zoom helper for Predictions map. When enabled, fits the map to:
// - the airport + all inbound markers, padded
// - minimum 100 km radius around the airport (so when all aircraft are inside
//   100 km we don't over-zoom to a tiny window)
// Re-fits on marker-list changes and every 15 s.
// Apply the SAME brightness/contrast filter as the Live map (Map.js
// TileFilterController) so the Predictions basemap darkness matches Live.
function PredictionsTileFilter({ filter }) {
  const map = useMap();
  useEffect(() => {
    const tilePane = map.getPane('tilePane');
    if (tilePane) tilePane.style.filter = filter;
  }, [map, filter]);
  return null;
}

/**
 * Map zoom presets. These frame the MAP only — the table below is scoped by the
 * range filter and is untouched by anything here.
 *
 * `acrossKm` is the width of the framed area. The first two are stated as
 * diameters and the third as a range, which is how an operator thinks about them:
 * "the airport", "the local area", "everything within 200 km".
 */
const ZOOM_PRESETS = [
  { id: 'airport',  label: 'Airport',  acrossKm: 20,  title: 'Frame the airport — 20 km across' },
  { id: 'local',    label: 'Local',    acrossKm: 100, title: 'Local area — 100 km across' },
  { id: 'regional', label: 'Regional', acrossKm: 400, title: 'Regional — 200 km range from the airport' },
  { id: 'all',      label: 'All',      acrossKm: null, title: 'Fit every inbound, including long-range' },
];

function PredictionsAutoZoom({ mode, markers, airport, expandedKey }) {
  const map = useMap();
  // markers update every second (upstream useMemo depends on nowMs). Listing
  // them in the effect deps caused fitBounds(animate:true) to fire every
  // second → visible map flicker. Read markers via ref so the periodic
  // fit picks up the latest set without retriggering the effect.
  const markersRef = useRef(markers);
  markersRef.current = markers;
  useEffect(() => {
    if (mode === 'none' || !map || !airport) return;

    // A fixed-radius preset is a framing the user chose, so it is applied once.
    // Re-fitting it on a timer would snap the map back every 15 s and make the
    // map impossible to pan or zoom by hand.
    const preset = ZOOM_PRESETS.find(p => p.id === mode && p.acrossKm != null);
    if (preset) {
      map.fitBounds(
        L.latLng(airport.lat, airport.lon).toBounds(preset.acrossKm * 1000),
        { padding: [20, 20], animate: true });
      return undefined;
    }

    const fit = (animate) => {
      const ms = markersRef.current || [];
      let pts = [[airport.lat, airport.lon]];
      if (mode === 'all') {
        for (const m of ms) if (m.lat != null && m.lon != null) pts.push([m.lat, m.lon]);
      } else if (mode === 'selected') {
        const sel = ms.find(m => m._predictionKey === expandedKey);
        if (sel && sel.lat != null && sel.lon != null) pts.push([sel.lat, sel.lon]);
      }
      let bounds = L.latLngBounds(pts);
      if (mode === 'all') {
        // Min 100 km around airport so a tight cluster doesn't over-zoom
        const MIN_KM = 100;
        const degLat = MIN_KM / 111;
        const degLon = MIN_KM / (111 * Math.cos((airport.lat * Math.PI) / 180));
        bounds.extend([airport.lat - degLat, airport.lon - degLon]);
        bounds.extend([airport.lat + degLat, airport.lon + degLon]);
      }
      // No maxZoom on 'all': the cap existed to stop a tight cluster filling the
      // screen, which the 100 km floor above already handles, and it is fitBounds'
      // zoom-OUT that a 900 NM inbound needs. Leaving it at 10 was harmless only
      // while nothing beyond 250 km had a marker.
      map.fitBounds(bounds, { padding: [40, 40], animate, maxZoom: 10 });
    };
    fit(true);  // initial fit can animate
    const id = setInterval(() => fit(false), 15000);  // periodic re-fit, no flicker
    return () => clearInterval(id);
  }, [mode, airport, expandedKey, map]);
  return null;
}

function Predictions({ predictionUpdate, departureForecastUpdate, liveAircraft, selectedReceiverUuid, onJumpToHistory }) {
  const { mapSettings, unitsSettings } = useSettings();
  const unitSystem = unitsSettings.units;
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;

  // Tab: Arrivals | Departures | On-Ground. The Airport page used to
  // host on-ground separately; per the merge plan it now lives here so
  // the user has a single ops surface for arrivals → on-ground →
  // departures lifecycle.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get('tab');
      if (t === 'departures' || t === 'ground' || t === 'accuracy') return t;
      return 'arrivals';
    } catch { return 'arrivals'; }
  });
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (activeTab === 'arrivals') url.searchParams.delete('tab');
      else url.searchParams.set('tab', activeTab);
      window.history.replaceState({}, '', url);
    } catch { /* noop */ }
  }, [activeTab]);

  const [selectedAirport, setSelectedAirport] = useState('EIDW');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  // Range filter: NEAR (aircraft currently within 200 km of the destination
  // airport — the "on approach" view), LONG_RANGE (aircraft still > 200 km
  // out — useful for spotting flights that haven't started descent yet),
  // ALL (default — show everything regardless of distance).
  const [rangeFilter, setRangeFilter] = useState('ALL');
  const [aircraftList, setAircraftList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  // Collapse the (often large) Landed group so airborne traffic stays front-and-centre.
  const [landedCollapsed, setLandedCollapsed] = useState(false);
  // Tablet header compaction: the analytical map tools (Auto Zoom, Accuracy
  // Heatmap, model selector, Quality Trend, Clear Heatmap) collapse into a popover.
  const [toolsOpen, setToolsOpen] = useState(false);
  const [predictionEvents, setPredictionEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showAccuracyHeatmap, setShowAccuracyHeatmap] = useState(false);
  const [heatmapModel, setHeatmapModel] = useState(() => 'v13-conformal');
  const [accuracySectors, setAccuracySectors] = useState([]);
  const [maxMaeInSector, setMaxMaeInSector] = useState(0);
  const [overallMae, setOverallMae] = useState(0);
  // Default sort by ETA Time ascending — the predicted touchdown moment is
  // stable as time passes, unlike lastEtaMinutes which is "minutes-from-when-
  // prediction-was-made" and effectively goes stale.
  const [sortConfig, setSortConfig] = useState({ key: 'lastEtaTime', direction: 'asc' });
  const [filter, setFilter] = useState('all'); // 'all', 'landed', 'inflight'
  const [airlineFilter, setAirlineFilter] = useState('all'); // ICAO code or 'all'
  // 'none' | 'all' (fit all inbound, min 100km) | 'selected' (fit expanded flight + airport)
  const [autoZoom, setAutoZoom] = useState('none');

  // Local clock for the selected airport — ticks every second.
  const [airportNow, setAirportNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setAirportNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Resizable table/map split. 45% left the columns from Max Dist rightwards —
  // every accuracy column, and Status — off the edge at 1080p, which is the one
  // thing this table exists to show. The map stays perfectly usable at 40%: it is
  // framed on the airport, and the zoom presets are there for when it is not.
  const [tableWidth, setTableWidth] = useState(60);
  const [isResizing, setIsResizing] = useState(false);
  const [showQualityChart, setShowQualityChart] = useState(false);
  const isResizingRef = useRef(false);

  const airport = useMemo(
    () => SUPPORTED_AIRPORTS.find(a => a.icao === selectedAirport) || SUPPORTED_AIRPORTS[0],
    [selectedAirport]
  );

  // Set when a flight first appears over the WebSocket. Such a row only knows what
  // this page has seen since it loaded, so its first-detect checkpoint and max range
  // are wrong until the backend summary is read again (see the refresh below).
  const summaryStaleRef = useRef(false);
  const lastSummaryAtRef = useRef(0);

  // Fetch aircraft predictions for the date
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setExpandedKey(null);
      setPredictionEvents([]);
      // Clear immediately so we don't briefly render the previous airport's
      // aircraft on the new airport's map. Without this, switching from
      // EIDW (high-traffic) to LPPR (sparse) flashed Dublin-bound flights
      // on the map for a second before the LPPR fetch returned.
      setAircraftList([]);
      setAccuracySectors([]);
      try {
        const token = getAccessToken();
        lastSummaryAtRef.current = Date.now();
        summaryStaleRef.current = false;
        const response = await getPredictionsForDate(selectedAirport, selectedDate, token, { rangeFilter });
        if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
          setAircraftList(response.aircraft || []);
          setAvailableAirlinesFromBackend(response.availableAirlines || []);
          setArrivalFlow(response.arrivalFlow || null);
        } else {
          setError(response.header?.errors?.[0]?.message || 'Failed to fetch predictions');
          setAircraftList([]);
        }
      } catch (err) {
        console.error('Error fetching predictions:', err);
        setError(err.message || 'Network error');
        setAircraftList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedAirport, selectedDate, rangeFilter]);

  // Re-read the backend summary while today is on screen. The WebSocket merge keeps
  // ETAs live, but the checkpoints (first detect, 100 km, 10 km) and max range are
  // whole-flight facts only the backend has: without this, every flight that arrived
  // after page load showed its first-seen-here point as "1st det", or nothing. Runs
  // soon after a new flight appears and otherwise every few minutes; replaces rows
  // in place rather than clearing, so the table and map don't flash.
  useEffect(() => {
    if (selectedDate !== new Date().toISOString().slice(0, 10)) return undefined;
    let cancelled = false;
    let running = false;
    const keyOf = a => `${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`;
    const tick = async () => {
      const age = Date.now() - lastSummaryAtRef.current;
      if (running || age < SUMMARY_REFRESH_MIN_MS) return;
      if (!summaryStaleRef.current && age < SUMMARY_REFRESH_MAX_MS) return;
      running = true;
      lastSummaryAtRef.current = Date.now();
      summaryStaleRef.current = false;
      try {
        const response = await getPredictionsForDate(selectedAirport, selectedDate, getAccessToken(), { rangeFilter });
        if (cancelled) return;
        if (response.header?.apiStatus !== 'OK' && response.header?.apiStatus !== 'SUCCESS') {
          console.warn('Predictions summary refresh failed:', response.header?.errors?.[0]?.message);
          return;
        }
        const fresh = response.aircraft || [];
        const freshFlights = new Set(fresh.map(a => `${a.icaoAddress}|${a.callsign || ''}`));
        setAircraftList(prev => [
          ...fresh,
          // A flight so new the summary missed it keeps its WebSocket row.
          ...prev.filter(a => !freshFlights.has(`${a.icaoAddress}|${a.callsign || ''}`)),
        ]);
        // The backend's firstPredictionAt can differ from the one a WebSocket row was
        // seeded with, which would collapse an expanded row — follow it to its new key.
        setExpandedKey(k => {
          if (!k || fresh.some(a => keyOf(a) === k)) return k;
          const [icao, cs] = k.split('|');
          const match = fresh.find(a => a.icaoAddress === icao && (a.callsign || '') === cs);
          return match ? keyOf(match) : k;
        });
      } catch (err) {
        console.warn('Predictions summary refresh failed:', err);
      } finally {
        running = false;
      }
    };
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedAirport, selectedDate, rangeFilter]);

  // Fetch accuracy heatmap
  useEffect(() => {
    if (!showAccuracyHeatmap) {
      setAccuracySectors([]);
      return;
    }
    const fetchHeatmap = async () => {
      try {
        const token = getAccessToken();
        // EIDW supports v11 / v12-quantile / v12-conformal; LPPR only v2.
        // The heatmap model is user-selectable via the dropdown next to the
        // Accuracy Heatmap button — that's the model whose accuracy sectors
        // get shaded on the map.
        const modelVersion = selectedAirport === 'EIDW' ? heatmapModel : 'v2';
        const response = await getPredictionAccuracyHeatmap(selectedAirport, 'metric', modelVersion, token);
        if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
          setAccuracySectors(response.sectors || []);
          setMaxMaeInSector(response.maxMaeInSector || 0);
          setOverallMae(response.overallMae || 0);
        }
      } catch (err) {
        console.error('Error fetching accuracy heatmap:', err);
        setAccuracySectors([]);
      }
    };
    fetchHeatmap();
  }, [selectedAirport, showAccuracyHeatmap, heatmapModel]);

  // Live WebSocket merge — new prediction events for the displayed airport/date
  // arrive here and update the aircraft list and (if expanded) the event detail table
  // without re-fetching.
  useEffect(() => {
    if (!predictionUpdate || !Array.isArray(predictionUpdate.events)) return;

    // Only keep events for the displayed airport and date. The WS broadcast is global,
    // so we must filter client-side.
    const events = predictionUpdate.events.filter(ev => {
      if (ev.airportIcao !== selectedAirport) return false;
      const evDate = (ev.predictionAt || '').slice(0, 10);
      return evDate === selectedDate;
    });
    if (events.length === 0) return;

    // Group by flight key (icao|callsign)
    const byFlight = new Map();
    for (const ev of events) {
      const k = `${ev.icaoAddress}|${ev.callsign || ''}`;
      if (!byFlight.has(k)) byFlight.set(k, []);
      byFlight.get(k).push(ev);
    }

    // Merge into aircraft summary rows. Split into ESTIMATE vs LANDED so LANDED
    // events don't pollute predictionCount / lastEta* fields (those should track
    // estimator output only) — a LANDED event just flips actualLandedAt.
    //
    // Each prediction cycle emits up to 3 events per flight (v11, v12-quantile,
    // v12-conformal). We keep v11's scalar as the headline lastEta*, and take
    // the distribution from v12-conformal (falling back to v12-quantile if the
    // calibrated flavour is absent). v2 flights (LPPR) have neither v12 model
    // and get the legacy scalar-only display.
    setAircraftList(prev => {
      const list = prev.slice();
      for (const [key, flightEvents] of byFlight.entries()) {
        const estimates = flightEvents.filter(ev => ev.eventType !== 'LANDED');
        const landedEvent = flightEvents.find(ev => ev.eventType === 'LANDED');
        // Version-agnostic: a "*-conformal"/"*-quantile" tag carries the distribution
        // (v12-conformal, v13-conformal, …); everything else is a scalar headline row.
        const isDistEvent = ev => ev.modelVersion && (ev.modelVersion.endsWith('-conformal') || ev.modelVersion.endsWith('-quantile'));
        const scalarEstimates = estimates.filter(ev => !isDistEvent(ev));
        const v12Conformal = [...estimates].reverse().find(ev => ev.modelVersion && ev.modelVersion.endsWith('-conformal'));
        const v12Quantile = [...estimates].reverse().find(ev => ev.modelVersion && ev.modelVersion.endsWith('-quantile'));
        const v12ForDist = v12Conformal || v12Quantile || null;
        // lastScalar drives lastEtaMinutes / lastEtaTime. If this cycle only
        // carried v12 rows (shouldn't happen in practice, but be defensive),
        // fall back to the newest estimate of any flavour.
        const lastScalar = scalarEstimates.length > 0
          ? scalarEstimates[scalarEstimates.length - 1]
          : (estimates.length > 0 ? estimates[estimates.length - 1] : null);
        const last = lastScalar;
        const idx = list.findIndex(a => `${a.icaoAddress}|${a.callsign || ''}` === key);
        if (idx >= 0) {
          const existing = list[idx];
          const merged = { ...existing };
          if (last) {
            // Only count scalar (v11/v2) rows so the prediction count reflects
            // "prediction cycles", not the 3× row-fan-out from v12 parallel runs.
            const cycleCount = scalarEstimates.length > 0 ? scalarEstimates.length : estimates.length;
            const predictionCount = (existing.predictionCount || 0) + cycleCount;
            const firstEta = existing.firstEtaMinutes != null
              ? existing.firstEtaMinutes
              : last.etaMinutes;
            merged.predictionCount = predictionCount;
            merged.lastPredictionAt = last.predictionAt;
            merged.lastEtaMinutes = last.etaMinutes;
            merged.lastEtaTime = last.etaTime;
            merged.firstEtaMinutes = firstEta;
            merged.etaChangeMinutes = firstEta != null ? last.etaMinutes - firstEta : null;
            merged.maxDistanceKm = Math.max(existing.maxDistanceKm || 0, last.distanceKm || 0);
            // Where the aircraft is NOW, as opposed to how far out it was first seen.
            // minDistanceKm from the summary is the closest approach so far, which is the
            // same thing for an inbound, but it goes stale between reloads - the live
            // event is the current answer.
            merged.currentDistanceKm = last.distanceKm != null
              ? last.distanceKm
              : existing.currentDistanceKm;
            Object.assign(merged, mergeLiveHold(merged, last));
            merged.registration = existing.registration || last.registration;
            merged.airlineIcao = existing.airlineIcao || last.airlineIcao;
            merged.aircraftType = existing.aircraftType || last.aircraftType;
            merged.iataFlightNumber = existing.iataFlightNumber || last.iataFlightNumber;
            // Advance the map position to the latest prediction snapshot. Without
            // this, markers for aircraft not in the live ADS-B feed (long-range
            // inbounds tracked only via predictions) stay frozen at the position
            // from the initial REST fetch, even though the backend emits a fresh
            // lat/lon every 20 s. Only overwrite when the event actually carries a
            // position so a partial payload can't null out a good coordinate.
            if (last.latitude != null && last.longitude != null) {
              merged.lastLatitude = last.latitude;
              merged.lastLongitude = last.longitude;
            }
            if (last.altitudeFeet != null) merged.lastAltitudeFeet = last.altitudeFeet;
            if (last.bearingFromAirport != null) merged.lastBearingFromAirport = last.bearingFromAirport;
          }
          if (v12ForDist) {
            merged.distributionJson = v12ForDist.distributionJson;
            merged.distributionModelVersion = v12ForDist.modelVersion;
            merged.distributionPredictionAt = v12ForDist.predictionAt;
            merged.etaP10Minutes = v12ForDist.etaP10Minutes;
            merged.etaP50Minutes = v12ForDist.etaP50Minutes;
            merged.etaP90Minutes = v12ForDist.etaP90Minutes;
          }
          // Distance-checkpoint updates from each estimate event. Backfills
          // the columns when WS arrives before the next REST refresh — without
          // this, live flights that started after page load show "—" until
          // their REST summary lands. Only fill empty checkpoints; once set,
          // they stick (each band represents the FIRST crossing). Every estimate
          // counts, not only the scalar rows: close in, most cycles carry only the
          // -conformal row, and the backend's checkpoint query reads both — so a
          // scalar-only walk left 100 km / 10 km blank on exactly the flights
          // that had crossed them.
          for (const ev of estimates) {
            if (!merged.firstDetectEtaTime) {
              merged.firstDetectPredictionAt = ev.predictionAt;
              merged.firstDetectDistanceKm   = ev.distanceKm;
              merged.firstDetectEtaTime      = ev.etaTime;
              merged.firstDetectEtaMinutes   = ev.etaMinutes;
            }
            if (!merged.checkpoint100EtaTime && ev.distanceKm != null && ev.distanceKm <= 100) {
              merged.checkpoint100PredictionAt = ev.predictionAt;
              merged.checkpoint100DistanceKm   = ev.distanceKm;
              merged.checkpoint100EtaTime      = ev.etaTime;
              merged.checkpoint100EtaMinutes   = ev.etaMinutes;
            }
            if (!merged.checkpoint10EtaTime && ev.distanceKm != null && ev.distanceKm <= 10) {
              merged.checkpoint10PredictionAt = ev.predictionAt;
              merged.checkpoint10DistanceKm   = ev.distanceKm;
              merged.checkpoint10EtaTime      = ev.etaTime;
              merged.checkpoint10EtaMinutes   = ev.etaMinutes;
            }
          }
          if (landedEvent) {
            merged.actualLandedAt = landedEvent.predictionAt;
            // Backend attaches live error stats to the LANDED broadcast so
            // the error columns update without a page refresh. Only overwrite
            // when the field is present (null-safe).
            if (landedEvent.meanAbsoluteErrorMinutes != null) {
              merged.meanAbsoluteErrorMinutes = landedEvent.meanAbsoluteErrorMinutes;
            }
            if (landedEvent.maxErrorMinutes != null) {
              merged.maxErrorMinutes = landedEvent.maxErrorMinutes;
            }
            if (landedEvent.earlyPredictionErrorMinutes != null) {
              merged.earlyPredictionErrorMinutes = landedEvent.earlyPredictionErrorMinutes;
            }
            if (landedEvent.finalErrorMinutes != null) {
              merged.finalErrorMinutes = landedEvent.finalErrorMinutes;
            }
            // Once the real landing is confirmed, the summary is no longer
            // "estimated" — ensures any italic/pending styling clears.
            merged.estimatedLanding = false;
          }
          list[idx] = merged;
        } else {
          // New flight that wasn't in the initial fetch — prepend a summary row.
          // Skip creation if the only event we got is a LANDED with no estimates
          // (shouldn't happen in practice, but be defensive).
          if (estimates.length === 0) continue;
          const firstScalar = scalarEstimates.length > 0 ? scalarEstimates[0] : estimates[0];
          const lastScalarInsert = scalarEstimates.length > 0
            ? scalarEstimates[scalarEstimates.length - 1]
            : estimates[estimates.length - 1];
          // Walk the events to capture the first crossing of each band as
          // the flight unfolds — same logic as the existing-flight branch
          // but applied to the brand-new entry.
          const newRow = {
            icaoAddress: firstScalar.icaoAddress,
            callsign: firstScalar.callsign,
            iataFlightNumber: firstScalar.iataFlightNumber,
            airlineIcao: firstScalar.airlineIcao,
            registration: firstScalar.registration,
            aircraftType: firstScalar.aircraftType,
            predictionCount: scalarEstimates.length > 0 ? scalarEstimates.length : estimates.length,
            firstPredictionAt: firstScalar.predictionAt,
            lastPredictionAt: lastScalarInsert.predictionAt,
            maxDistanceKm: Math.max(0, ...estimates.map(ev => ev.distanceKm || 0)) || lastScalarInsert.distanceKm,
            currentDistanceKm: lastScalarInsert.distanceKm,
            firstEtaMinutes: firstScalar.etaMinutes,
            lastEtaMinutes: lastScalarInsert.etaMinutes,
            lastEtaTime: lastScalarInsert.etaTime,
            etaChangeMinutes: lastScalarInsert.etaMinutes - firstScalar.etaMinutes,
            // Seed the map position from the newest event. A flight that first
            // appears via WebSocket (started after page load) has no REST summary
            // yet, so without this its marker has no coordinate at all and never
            // renders until the next REST refresh.
            lastLatitude: lastScalarInsert.latitude,
            lastLongitude: lastScalarInsert.longitude,
            lastAltitudeFeet: lastScalarInsert.altitudeFeet,
            lastBearingFromAirport: lastScalarInsert.bearingFromAirport,
            actualLandedAt: landedEvent ? landedEvent.predictionAt : null,
            distributionJson: v12ForDist ? v12ForDist.distributionJson : null,
            distributionModelVersion: v12ForDist ? v12ForDist.modelVersion : null,
            distributionPredictionAt: v12ForDist ? v12ForDist.predictionAt : null,
            etaP10Minutes: v12ForDist ? v12ForDist.etaP10Minutes : null,
            etaP50Minutes: v12ForDist ? v12ForDist.etaP50Minutes : null,
            etaP90Minutes: v12ForDist ? v12ForDist.etaP90Minutes : null,
          };
          // Seeded from what this page has seen since it loaded, which for a flight
          // first detected earlier is NOT the true first detection — the periodic
          // summary refresh below replaces it with the backend's answer.
          for (const ev of estimates) {
            if (!newRow.firstDetectEtaTime) {
              newRow.firstDetectPredictionAt = ev.predictionAt;
              newRow.firstDetectDistanceKm   = ev.distanceKm;
              newRow.firstDetectEtaTime      = ev.etaTime;
              newRow.firstDetectEtaMinutes   = ev.etaMinutes;
            }
            if (!newRow.checkpoint100EtaTime && ev.distanceKm != null && ev.distanceKm <= 100) {
              newRow.checkpoint100PredictionAt = ev.predictionAt;
              newRow.checkpoint100DistanceKm   = ev.distanceKm;
              newRow.checkpoint100EtaTime      = ev.etaTime;
              newRow.checkpoint100EtaMinutes   = ev.etaMinutes;
            }
            if (!newRow.checkpoint10EtaTime && ev.distanceKm != null && ev.distanceKm <= 10) {
              newRow.checkpoint10PredictionAt = ev.predictionAt;
              newRow.checkpoint10DistanceKm   = ev.distanceKm;
              newRow.checkpoint10EtaTime      = ev.etaTime;
              newRow.checkpoint10EtaMinutes   = ev.etaMinutes;
            }
          }
          list.unshift(mergeLiveHold(newRow, lastScalarInsert));
          summaryStaleRef.current = true;
        }
      }

      return list;
    });

    // If the expanded flight received new events, append them to the detail table.
    // KEY-FORMAT NOTE: expandedKey is 3-part (icao|callsign|firstPredictionAt)
    // because the same (icao, callsign) pair can legitimately fly multiple
    // sessions per day and we need them to be distinct expansion targets.
    // byFlight is keyed 2-part (icao|callsign) since the WS broadcast has no
    // session identifier. Looking up byFlight.get(expandedKey) directly
    // therefore never matches — the symptom was "new prediction rows never
    // appear in the expanded detail table after selecting a flight, even
    // though the backend is still emitting every 20 s."
    // Resolution: derive the 2-part key by splitting expandedKey, look up,
    // and append. If the aircraft started a new session mid-expansion we
    // could technically end up appending rows from a different session into
    // the detail table, but that's far better than showing nothing and in
    // practice only occurs if the user leaves a row expanded for >30 min
    // across a landing + new flight under the same callsign.
    if (expandedKey) {
      const keyParts = expandedKey.split('|');
      const flightKey = (keyParts.length >= 2)
        ? `${keyParts[0]}|${keyParts[1]}`
        : expandedKey;
      const forExpanded = byFlight.get(flightKey);
      if (forExpanded && forExpanded.length > 0) {
        setPredictionEvents(prev => [...prev, ...forExpanded]);
      }
    }
  }, [predictionUpdate, selectedAirport, selectedDate, expandedKey]);

  // Periodic staleness prune. The backend's getAircraftSummaryForDate filter
  // drops rows with lastPredictionAt > 30 min ago and no actualLandedAt, but
  // the FE keeps every row once it's been received via WS. Without a client
  // prune, ghost rows from coverage gaps / mid-flight callsign changes /
  // backend restarts accumulate and stay visible as "LIVE" forever.
  //
  // Two rules applied every 30 s:
  //   1. Hard staleness — drop rows with no new predictions in 10 min.
  //      Live aircraft emit every 20 s, so 10 min of silence is unambiguous.
  //   2. Icao supersession — when a new (icao, callsign) row exists with a
  //      newer lastPredictionAt for the same hex, the older callsign was a
  //      stale-callsign-inheritance ghost (transponder broadcast cached
  //      callsign before its real ident arrived). Drop it as soon as the
  //      newer callsign has overtaken it by ≥ 60 s — without waiting for
  //      the 10-minute hard timeout.
  useEffect(() => {
    const handle = setInterval(() => {
      const now = Date.now();
      const STALE_MS = 10 * 60 * 1000;
      const SUPERSEDE_MS = 60 * 1000;
      setAircraftList(prev => {
        // Build per-icao newest lastPredictionAt for the supersession check.
        const newestByIcao = new Map();
        for (const a of prev) {
          const t = parseServerTime(a.lastPredictionAt)?.getTime();
          if (t == null) continue;
          const cur = newestByIcao.get(a.icaoAddress);
          if (cur == null || t > cur) newestByIcao.set(a.icaoAddress, t);
        }
        let changed = false;
        const filtered = prev.filter(a => {
          if (a.actualLandedAt) return true;
          const last = parseServerTime(a.lastPredictionAt)?.getTime();
          if (last == null) return true;
          if ((now - last) > STALE_MS) { changed = true; return false; }
          const newest = newestByIcao.get(a.icaoAddress);
          if (newest != null && newest - last >= SUPERSEDE_MS) {
            changed = true; return false;
          }
          return true;
        });
        return changed ? filtered : prev;
      });
    }, 30_000);
    return () => clearInterval(handle);
  }, []);

  // Fetch detail history when row is expanded.
  // Each row is one FLIGHT (icao + callsign), so we pass both to filter correctly.
  const handleExpandFlight = useCallback(async (icaoAddress, callsign, firstPredictionAt) => {
    // Key must include firstPredictionAt so two sessions with the same (icao,
    // callsign) are distinct.
    const key = `${icaoAddress}|${callsign || ''}|${firstPredictionAt || ''}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      setPredictionEvents([]);
      return;
    }
    setExpandedKey(key);
    setEventsLoading(true);
    setPredictionEvents([]);
    const summary = aircraftList.find(a =>
      a.icaoAddress === icaoAddress
      && (a.callsign || '') === (callsign || '')
      && (a.firstPredictionAt || '') === (firstPredictionAt || ''));
    const sessionStart = summary?.firstPredictionAt || null;
    // Only pass an upper bound when the flight has actually landed. For
    // active flights, summary.lastPredictionAt is whatever was true when
    // getPredictionsForDate last polled — passing it here would clip every
    // prediction emitted between that snapshot and the moment the user
    // expanded the row, leaving an artificial gap in the table that the
    // websocket-append fallback then can't fill (it only tracks predictions
    // arriving AFTER the subscription, never the in-between ones).
    const sessionEnd = summary?.actualLandedAt ? summary?.lastPredictionAt : null;
    try {
      const token = getAccessToken();
      const response = await getPredictionHistory(icaoAddress, callsign, selectedAirport, selectedDate, token, sessionStart, sessionEnd);
      if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
        // Backend returns events in chronological order — keep that for the map polyline,
        // but the events table renders them DESC (latest first) so we sort there.
        setPredictionEvents(response.events || []);
      }
    } catch (err) {
      console.error('Error fetching prediction history:', err);
    } finally {
      setEventsLoading(false);
    }
  }, [expandedKey, selectedAirport, selectedDate]);

  // Events sorted descending for the table view (latest prediction at top).
  // If the aircraft has landed but no LANDED event exists in the data,
  // inject a synthetic one so the touchdown time is always visible.
  const eventsForTable = useMemo(() => {
    const events = [...predictionEvents];
    const summary = expandedKey
      ? aircraftList.find(a => `${a.icaoAddress}|${a.callsign || ''}` === expandedKey)
      : null;
    if (summary?.actualLandedAt && !events.some(e => e.eventType === 'LANDED')) {
      events.push({
        eventType: 'LANDED',
        predictionAt: summary.actualLandedAt,
        etaMinutes: 0,
        etaTime: summary.actualLandedAt,
        distanceKm: 0,
        distanceNm: 0,
        bearingFromAirport: 0,
        altitudeFeet: null,
        latitude: null,
        longitude: null,
      });
    }
    return events.sort((a, b) => {
      const ta = parseServerTime(a.predictionAt).getTime();
      const tb = parseServerTime(b.predictionAt).getTime();
      return tb - ta;
    });
  }, [predictionEvents, expandedKey, aircraftList]);

  // Available airline codes for the filter dropdown.
  // Combine (a) airlines the backend identified as having arrivals today —
  // includes landed flights no longer in predictions + scheduled arrivals —
  // with (b) airlines in the current aircraftList for completeness.
  const [availableAirlinesFromBackend, setAvailableAirlinesFromBackend] = useState([]);
  // Landing interval + runway wind (backend ArrivalFlow); today only, null otherwise.
  const [arrivalFlow, setArrivalFlow] = useState(null);
  const airlineOptions = useMemo(() => {
    const set = new Set(availableAirlinesFromBackend);
    for (const a of aircraftList) if (a.airlineIcao) set.add(a.airlineIcao);
    return [...set].sort();
  }, [aircraftList, availableAirlinesFromBackend]);

  // Sort and filter aircraft list
  const sortedAircraft = useMemo(() => {
    // Defensive dedup — WS merges and race conditions can leave duplicate rows
    // in aircraftList. Keep the entry with the most recent lastPredictionAt.
    // Also annotate each row with `_presumedLanded` so the sort and the Status
    // cell use a single derived flag instead of recomputing it everywhere.
    const dedup = new Map();
    const nowMs = airportNow.getTime();
    for (const a of aircraftList) {
      const k = `${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`;
      const prev = dedup.get(k);
      if (!prev
          || (a.lastPredictionAt || '') > (prev.lastPredictionAt || '')) {
        const etaPastMin = a.lastEtaTime
          ? (nowMs - parseServerTime(a.lastEtaTime).getTime()) / 60000
          : 0;
        // How long since the backend last predicted for this flight. A flight
        // whose predictions stopped is a flight we lost contact with — we do
        // not know what happened to it.
        const predAgeMin = a.lastPredictionAt
          ? (nowMs - parseServerTime(a.lastPredictionAt).getTime()) / 60000
          : Infinity;
        const stale = predAgeMin > STALE_CONTACT_MIN;
        // Presume a landing ONLY while we were still tracking it. Previously this
        // was "the predicted ETA passed 5 minutes ago", full stop — so a flight we
        // lost at 105 NM was silently filed as landed, with no movement record and
        // no evidence, and counted in the landed total. The backend learned this in
        // 1.0.443 ("presume a landing only while the last fix is under an hour old");
        // this is the same rule on the display side.
        const presumed = !a.actualLandedAt && etaPastMin > 5 && !stale;
        dedup.set(k, { ...a, _presumedLanded: presumed, _stale: stale, _predAgeMin: predAgeMin, _holdRank: holdRank(a) });
      }
    }
    let filtered = [...dedup.values()];
    if (filter === 'landed') {
      filtered = filtered.filter(a => a.actualLandedAt || a._presumedLanded);
    } else if (filter === 'inflight') {
      filtered = filtered.filter(a => !a.actualLandedAt && !a._presumedLanded);
    }
    if (airlineFilter !== 'all') {
      filtered = filtered.filter(a => a.airlineIcao === airlineFilter);
    }

    // For the two ETA sort columns, apply the "in-flight first, landed bucket by
    // touchdown time desc" grouping regardless of direction so the table stays
    // readable. Other columns: plain sort.
    const isEtaSort = sortConfig.key === 'lastEtaMinutes' || sortConfig.key === 'lastEtaTime';
    return [...filtered].sort((a, b) => {
      if (isEtaSort) {
        // Treat presumed-landed as landed so the two groups are (truly-inflight)
        // and (landed-or-presumed-landed). Prevents a separate "Landed (est)"
        // section from forming at the top of the table.
        const aLanded = !!a.actualLandedAt || a._presumedLanded;
        const bLanded = !!b.actualLandedAt || b._presumedLanded;
        if (aLanded !== bLanded) return aLanded ? 1 : -1; // in-flight before landed
        if (aLanded) {
          // Both landed: most recent landing first.
          // Use actualLandedAt if available; otherwise fall back to lastEtaTime
          // (the presumed landing moment). That keeps presumed rows temporally
          // sorted alongside real ones.
          const aT = a.actualLandedAt || a.lastEtaTime || '';
          const bT = b.actualLandedAt || b.lastEtaTime || '';
          return bT.localeCompare(aT);
        }
        // Out of contact goes below everything we are still tracking, most recently
        // heard first. Its ETA is a number we no longer trust, and sorting on it put
        // these rows at the top — an ETA that is missing compared as '' and won.
        if (a._stale !== b._stale) return a._stale ? 1 : -1;
        if (a._stale) return (a._predAgeMin ?? Infinity) - (b._predAgeMin ?? Infinity);
        // Both in-flight: sort by the chosen ETA key — parsed, not string-compared,
        // and a missing ETA last in either direction.
        if (sortConfig.key === 'lastEtaTime') {
          const av = parseServerTime(a.lastEtaTime)?.getTime();
          const bv = parseServerTime(b.lastEtaTime)?.getTime();
          if (av == null || Number.isNaN(av)) return (bv == null || Number.isNaN(bv)) ? 0 : 1;
          if (bv == null || Number.isNaN(bv)) return -1;
          return sortConfig.direction === 'asc' ? av - bv : bv - av;
        }
      }
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [aircraftList, sortConfig, filter, airlineFilter]);

  // Counts for the In-Flight / Landed group dividers in the arrivals table.
  const inFlightTotal = useMemo(
    () => sortedAircraft.filter(a => !(a.actualLandedAt || a._presumedLanded)).length,
    [sortedAircraft]);
  const landedTotal = sortedAircraft.length - inFlightTotal;

  // Predicted landing ORDER for in-flight aircraft — ranked by ETA so the number
  // is stable regardless of how the table is sorted (1 = lands next). Landed
  // flights have no order, and neither do flights we lost contact with: we do not
  // know where they are, so numbering them would push every live flight down one.
  // Keyed by the flight's row key.
  const arrivalOrderByKey = useMemo(() => {
    const m = new Map();
    sortedAircraft
      .filter(a => !(a.actualLandedAt || a._presumedLanded || a._stale))
      .slice()
      // Rank by absolute predicted landing TIME (same key the table is sorted by)
      // — not lastEtaMinutes, which is relative to each flight's last prediction
      // and goes stale at different rates, causing Seq to disagree with row order.
      .sort((a, b) => {
        const ta = parseServerTime(a.lastEtaTime)?.getTime() ?? Infinity;
        const tb = parseServerTime(b.lastEtaTime)?.getTime() ?? Infinity;
        return ta - tb;
      })
      .forEach((a, i) => m.set(`${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`, i + 1));
    return m;
  }, [sortedAircraft]);

  // Live late-probability trend per flight. As the live feed updates each cycle
  // we record the latest late% (vs schedule) and the previous cycle's, so the
  // row can show whether delay risk is rising or falling. Keyed by flight key.
  const lateTrendRef = useRef(new Map());
  useEffect(() => {
    const m = lateTrendRef.current;
    for (const a of aircraftList) {
      if (a.actualLandedAt || a._presumedLanded) continue;
      const o = arrivalOutlook({
        distributionJson: a.distributionJson,
        predictionAt: a.distributionPredictionAt || a.lastPredictionAt,
        scheduledArrival: a.scheduledArrival,
        airlineEstimatedArrival: a.airlineEstimatedArrival,
      });
      if (!o) continue;
      const key = `${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`;
      const base = a.distributionPredictionAt || a.lastPredictionAt || '';
      const rec = m.get(key);
      if (!rec) m.set(key, { curBase: base, curLate: o.late, prevLate: null });
      else if (rec.curBase !== base) m.set(key, { curBase: base, curLate: o.late, prevLate: rec.curLate });
    }
  }, [aircraftList]);
  const lateTrendDelta = (a) => {
    const rec = lateTrendRef.current.get(`${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`);
    return rec && rec.prevLate != null ? rec.curLate - rec.prevLate : null;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortProps = { config: sortConfig, onSort: handleSort };
  const destIata = SUPPORTED_AIRPORTS.find(ap => ap.icao === selectedAirport)?.iata
    || icaoToIata(selectedAirport) || selectedAirport;
  const liveFor = (a) => (liveAircraft && a.icaoAddress
    ? (liveAircraft[a.icaoAddress.toLowerCase()] || liveAircraft[a.icaoAddress.toUpperCase()] || liveAircraft[a.icaoAddress])
    : null);

  // Checkpoint cell. In flight: the ETA predicted at that checkpoint / the range it was
  // made at. Landed: the signed error vs the actual landing (+ = predicted late) / the
  // time that was predicted.
  const renderCheckpoint = (a, etaTime, distKm, label) => {
    const eta = etaTime ? parseServerTime(etaTime) : null;
    if (!eta) {
      return (
        <td className="numeric" data-label={label}>
          <div className="pt-stack"><div className="pt-l1 pt-muted">—</div><div className="pt-l2">{' '}</div></div>
        </td>
      );
    }
    const landed = a.actualLandedAt ? parseServerTime(a.actualLandedAt) : null;
    const distTxt = distKm != null ? `${convertDistance(distKm, unitSystem).toFixed(0)} ${units.distance}` : ' ';
    if (landed) {
      const deltaMin = (eta.getTime() - landed.getTime()) / 60000;
      return (
        <td className="numeric" data-label={label}
            title={`${label}${distKm != null ? ` (${distKm.toFixed(0)} km out)` : ''}: predicted ${formatLocalHourMin(etaTime, selectedAirport)} vs actual ${formatLocalHourMin(a.actualLandedAt, selectedAirport)} (Δ ${formatDelta(deltaMin)})`}>
          <div className="pt-stack">
            <div className={`pt-l1 pt-err ${errorToBandClass(deltaMin)}`}>{formatDelta(deltaMin)}</div>
            {/* Range it was made at, as in flight — the predicted time is in the tooltip. */}
            <div className="pt-l2">{distTxt}</div>
          </div>
        </td>
      );
    }
    return (
      <td className="numeric" data-label={label}
          title={`${label}: predicted landing at ${formatLocalHourMin(etaTime, selectedAirport)} (distance ${distKm != null ? distKm.toFixed(0) + ' km' : '—'})`}>
        <div className="pt-stack">
          <div className="pt-l1">{formatLocalHourMin(etaTime, selectedAirport)}</div>
          <div className="pt-l2">{distTxt}</div>
        </div>
      </td>
    );
  };

  // Resize handlers for table/map split
  const handleResizeMouseDown = useCallback((e) => {
    isResizingRef.current = true;
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleResizeMouseMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    const windowWidth = window.innerWidth;
    const newTableWidth = ((windowWidth - e.clientX) / windowWidth) * 100;
    if (newTableWidth >= 20 && newTableWidth <= 70) {
      setTableWidth(newTableWidth);
    }
  }, []);

  const handleResizeMouseUp = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
  }, []);

  const handleResizeTouchStart = useCallback((e) => {
    isResizingRef.current = true;
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleResizeTouchMove = useCallback((e) => {
    if (!isResizingRef.current || !e.touches || e.touches.length === 0) return;
    const windowWidth = window.innerWidth;
    const newTableWidth = ((windowWidth - e.touches[0].clientX) / windowWidth) * 100;
    if (newTableWidth >= 20 && newTableWidth <= 70) {
      setTableWidth(newTableWidth);
    }
  }, []);

  const handleResizeTouchEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => handleResizeMouseMove(e);
    const onMouseUp = () => handleResizeMouseUp();
    const onTouchMove = (e) => handleResizeTouchMove(e);
    const onTouchEnd = () => handleResizeTouchEnd();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [handleResizeMouseMove, handleResizeMouseUp, handleResizeTouchMove, handleResizeTouchEnd]);

  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isResizing]);

  // Track polyline segments from prediction events. Break into multiple segments
  // whenever consecutive events are more than ~90 seconds apart — avoids drawing
  // a straight interpolation line across gaps (e.g., a backend restart leaving
  // a minute or two without predictions).
  const trackSegments = useMemo(() => {
    const GAP_THRESHOLD_MS = 90_000;
    const segments = [];
    let current = [];
    let prevTs = null;
    for (const e of predictionEvents) {
      if (e.latitude == null || e.longitude == null) continue;
      const ts = e.predictionAt ? parseServerTime(e.predictionAt).getTime() : null;
      if (prevTs != null && ts != null && ts - prevTs > GAP_THRESHOLD_MS) {
        if (current.length > 1) segments.push(current);
        current = [];
      }
      current.push([e.latitude, e.longitude]);
      prevTs = ts;
    }
    if (current.length > 1) segments.push(current);
    return segments;
  }, [predictionEvents]);

  // (Removed legacy `liveSelectedAircraft` — superseded by `liveInboundMarkers`
  // below which renders every live inbound aircraft and passes isSelected for
  // the expanded one.)
  // eslint-disable-next-line no-unused-vars
  const _legacyLiveSelected = useMemo(() => {
    if (!expandedKey || !liveAircraft) return null;
    const [icao] = expandedKey.split('|');
    if (!icao) return null;
    const hexLower = icao.toLowerCase();
    const hexUpper = icao.toUpperCase();
    const match = liveAircraft[hexLower] || liveAircraft[hexUpper] || liveAircraft[icao];
    if (!match || match.lat == null || match.lon == null) return null;
    const summary = aircraftList.find(a => `${a.icaoAddress}|${a.callsign || ''}` === expandedKey);
    if (summary && summary.actualLandedAt) return null;
    return match;
  }, [expandedKey, liveAircraft, aircraftList]);

  // All live inbound aircraft for the selected airport+date. Joins aircraftList
  // (flights with predictions today, not yet landed) with liveAircraft (live
  // position stream keyed by hex). Updates automatically as either source
  // changes via WebSocket (predictionUpdate → aircraftList, liveAircraft prop).
  //
  // Returns enriched aircraft-shape objects compatible with <AircraftMarker>,
  // with `flight` overridden to show the callsign + ETA, e.g. "EIN952 · 14:23",
  // so the label matches the table's headline.
  const liveInboundMarkers = useMemo(() => {
    const out = [];
    for (const a of aircraftList) {
      if (a.actualLandedAt) continue; // landed flights handled separately below
      if (!a.icaoAddress) continue;
      const hex = a.icaoAddress;
      // The selected (expanded) flight must ALWAYS get a marker — otherwise its
      // track renders with no aircraft icon. Bypass the airline/range/stale drop
      // guards for it; only the "no position at all" guard below still applies.
      const flightKey = `${hex}|${a.callsign || ''}|${a.firstPredictionAt || ''}`;
      const isExpandedFlight = expandedKey === flightKey;
      if (!isExpandedFlight && airlineFilter !== 'all' && a.airlineIcao !== airlineFilter) continue;
      const live = liveAircraft
        ? (liveAircraft[hex.toLowerCase()] || liveAircraft[hex.toUpperCase()] || liveAircraft[hex])
        : null;
      const hasLive = !!(live && live.lat != null);
      // If the live WS position is implausibly far from the selected
      // airport, the flight has moved on (departed earlier today, now
      // cruising to a different destination, picked up by a different
      // receiver). The (icao, callsign) is still in aircraftList because
      // it had EIDW predictions earlier today, but rendering its current
      // live position (e.g. near Lisbon) on the EIDW map is misleading.
      // 250 km is a generous bound that includes long final-approach
      // segments without admitting cruise-distant aircraft.
      // Freshness, not distance, is what separates the two cases. A flight the
      // backend is still predicting for THIS airport is inbound to it, however far
      // out it is — the France and Iberia receivers routinely hand us Dublin
      // arrivals at 700-900 NM. A flight that has moved on stops being predicted
      // here, and then the distance test is the right one.
      const lastPredAgeMin = a.lastPredictionAt
        ? (airportNow.getTime() - parseServerTime(a.lastPredictionAt).getTime()) / 60000
        : Infinity;
      if (hasLive && !isExpandedFlight && lastPredAgeMin > STALE_CONTACT_MIN) {
        const liveDistKm = Math.hypot(live.lat - airport.lat, live.lon - airport.lon) * 111;
        if (liveDistKm > 250) continue;
      }
      // Prefer live WS position; fall back to last known prediction_event position.
      // BUT suppress stale fallback-only markers that were last seen close to the
      // airport — those are almost certainly flights that landed without our system
      // detecting the LANDED event. Rendering them shows an on-approach heading in
      // a position where the aircraft no longer exists, which is misleading.
      if (!hasLive && !isExpandedFlight) {
        const lastPredMs = a.lastPredictionAt ? parseServerTime(a.lastPredictionAt).getTime() : 0;
        const staleMin = (airportNow.getTime() - lastPredMs) / 60000;
        // Presume-landed rule: no fresh WS + last known position < 15 km from
        // airport + predictions stopped > 3 min ago → hide.
        const closeToAirport = a.lastLatitude != null && a.lastLongitude != null
          && Math.hypot(a.lastLatitude - airport.lat, a.lastLongitude - airport.lon) * 111 < 15;
        if (staleMin > 3 && closeToAirport) continue;
        // Also skip if predictions are very stale regardless of position (the
        // aircraft has moved on, we just don't know where).
        if (staleMin > STALE_CONTACT_MIN) continue;
      }
      const lat = hasLive ? live.lat : a.lastLatitude;
      const lon = hasLive ? live.lon : a.lastLongitude;
      if (lat == null || lon == null) continue;
      // Heading fallback chain: live.track / heading / true_heading / mag_heading
      // → row-level lastHeadingDegrees → bearing-toward-airport (lastBearingFromAirport
      // + 180°, since lastBearingFromAirport is bearing FROM airport TO aircraft and
      // an inbound flight typically heads back toward the airport). Without these
      // fall-backs, every flight whose live WS payload was missing the heading
      // field rendered pointing north.
      let heading;
      if (live && live.track != null)               heading = live.track;
      else if (live && live.heading != null)        heading = live.heading;
      else if (live && live.true_heading != null)   heading = live.true_heading;
      else if (live && live.mag_heading != null)    heading = live.mag_heading;
      else if (a.lastHeadingDegrees != null)        heading = a.lastHeadingDegrees;
      else if (a.lastBearingFromAirport != null)    heading = (a.lastBearingFromAirport + 180) % 360;
      else                                          heading = 0;
      const altFt = live && live.alt_baro != null ? live.alt_baro : a.lastAltitudeFeet;
      const flightLabel = a.callsign || a.iataFlightNumber || hex.toUpperCase();
      // Show ETA TIME (HH:MM) on the marker label — much more useful than minutes
      const etaStr = a.lastEtaTime ? formatLocalHourMin(a.lastEtaTime, selectedAirport) : '';
      // Arrival-sequencer: if the ETA was pushed out to maintain landing
      // separation, mark the label with a ⏱ and the push delta so it's clear
      // the estimate is queue-adjusted, not the raw model output.
      const seqAdjusted = !!(live && live.eta_sequence_adjusted);
      const seqPushMin = (live && live.eta_raw_minutes != null && live.eta_minutes != null)
        ? (live.eta_minutes - live.eta_raw_minutes) : null;
      let flightLabelStr = etaStr ? `${flightLabel} · ${etaStr}` : flightLabel;
      if (seqAdjusted && etaStr) {
        flightLabelStr += seqPushMin != null && seqPushMin >= 1
          ? ` ⏱+${Math.round(seqPushMin)}` : ' ⏱';
      }
      const isStale = !live || live.lat == null;
      out.push({
        ...(live || {}),
        hex,
        lat, lon,
        track: heading,
        alt_baro: altFt,
        // AircraftMarker reads `t` for the sprite-sheet icon position.
        // Live WS usually carries it but during coverage gaps `live` is
        // null and the sprite would fall back to the default silhouette
        // for known aircraft like a B777. Pull from row data when live
        // doesn't have it. (Same pattern as PredictionsDepartures.)
        t: live?.t ?? a.aircraftType,
        airline_icao: a.airlineIcao,
        flight: flightLabelStr,
        // Tracked flights are never "new aircraft" in the live-feed sense —
        // they have a prediction history. Without this explicit flag, flights
        // whose live WS payload didn't include last_seen_at fell into the
        // default-green "new" colour filter at AircraftMarker.
        isNew: false,
        _predictionKey: flightKey,
        // Same number as the table's # column, so map and table read as one sequence.
        _seqNumber: arrivalOrderByKey.get(flightKey),
        _isExpanded: expandedKey === flightKey,
        _isStale: isStale
      });
    }
    return out;
  }, [aircraftList, liveAircraft, expandedKey, selectedAirport, airportNow, airlineFilter, arrivalOrderByKey]);

  // The expanded flight: the Live page's popup and its track, opened with the row. Fetched by hex
  // over a time window (any receiver), so a flight that has left the live feed still has one;
  // once landed the line runs from 2 km before touchdown to now.
  const expandedFlight = useMemo(() => {
    if (!expandedKey) return null;
    const keyOf = a => `${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`;
    return sortedAircraft.find(a => keyOf(a) === expandedKey)
      || aircraftList.find(a => keyOf(a) === expandedKey) || null;
  }, [expandedKey, sortedAircraft, aircraftList]);
  const landingTrack = useLandingTrack(useMemo(() => arrivalTrackQuery(expandedFlight), [expandedFlight]));

  // Build accuracy heatmap polygons
  const accuracyPolygons = useMemo(() => {
    if (!showAccuracyHeatmap || !accuracySectors.length) return [];
    return accuracySectors.map((sector, idx) => {
      const bearingStart = sector.bearingStart ?? 0;
      const bearingEnd = sector.bearingEnd ?? bearingStart + 15;
      const distanceStart = sector.distanceStart ?? 0;
      const distanceEnd = sector.distanceEnd ?? distanceStart + 50;

      const points = [];
      if (distanceStart === 0) {
        points.push([airport.lat, airport.lon]);
      } else {
        for (let b = bearingStart; b <= bearingEnd; b += 5) {
          points.push(calculatePoint(airport.lat, airport.lon, b, distanceStart));
        }
      }
      for (let b = bearingEnd; b >= bearingStart; b -= 5) {
        points.push(calculatePoint(airport.lat, airport.lon, b, distanceEnd));
      }
      if (distanceStart === 0) {
        points.push([airport.lat, airport.lon]);
      }

      const color = sector.hasLandingData ? maeToColor(sector.meanAbsoluteError, distanceStart) : '#3b82f6';
      // All sectors with data get a visible base opacity; high-count sectors become more saturated
      const countWeight = Math.min(1, Math.log10(1 + (sector.predictionCount || 0)) / 2);
      const opacity = sector.hasLandingData
        ? 0.5 + countWeight * 0.35
        : 0.25 + countWeight * 0.15;

      return {
        key: `acc-${idx}`,
        positions: points,
        color,
        fillOpacity: opacity,
        meanAbsoluteError: sector.meanAbsoluteError,
        rmse: sector.rmse,
        maxError: sector.maxError,
        predictionCount: sector.predictionCount,
        avgEtaMinutes: sector.avgEtaMinutes,
        minEtaMinutes: sector.minEtaMinutes,
        maxEtaMinutes: sector.maxEtaMinutes,
        hasLandingData: sector.hasLandingData,
        bearingStart,
        bearingEnd,
        distanceStart,
        distanceEnd
      };
    });
  }, [showAccuracyHeatmap, accuracySectors, maxMaeInSector, airport]);

  // Match the Live map exactly: same style URL + brightness/contrast filter.
  const tileConfig = MAP_STYLES[mapSettings.style] || getTileLayerConfig(mapSettings.tileLayer);
  const mapFilter = getMapFilter(mapSettings.brightness != null ? mapSettings.brightness : 60);

  // Stats summary
  const stats = useMemo(() => {
    const nowMs = airportNow.getTime();
    const isPresumed = a => !a.actualLandedAt && a.lastEtaTime
      && (nowMs - parseServerTime(a.lastEtaTime).getTime()) / 60000 > 5;
    const total = aircraftList.length;
    const landed = aircraftList.filter(a => a.actualLandedAt || isPresumed(a)).length;
    const inflight = total - landed;
    const totalPredictions = aircraftList.reduce((sum, a) => sum + (a.predictionCount || 0), 0);
    // Use mean absolute error (correct metric) instead of last-prediction-only error
    const errors = aircraftList
      .filter(a => a.meanAbsoluteErrorMinutes != null)
      .map(a => a.meanAbsoluteErrorMinutes);
    const avgError = errors.length ? errors.reduce((s, e) => s + e, 0) / errors.length : 0;

    // Active arrival runway — inferred from the bearing-from-airport of
    // flights that landed in the last 60 min. An aircraft on final approach
    // to runway 28 sits east of the airport (bearing ≈ 90°), to runway 10
    // sits west (bearing ≈ 270°). Direction is enough for EIDW's parallel
    // ops since 10L/10R (or 28L/28R) share the same heading.
    const headWindowMs = 60 * 60 * 1000;
    const cutoffMs = nowMs - headWindowMs;
    const dirCounts = { '10': 0, '28': 0, 'other': 0 };
    for (const a of aircraftList) {
      const t = a.actualLandedAt ? parseServerTime(a.actualLandedAt) : null;
      if (!t || t.getTime() < cutoffMs) continue;
      const b = a.lastBearingFromAirport;
      if (b == null) continue;
      const norm = ((b % 360) + 360) % 360;
      if (norm > 60 && norm < 130)        dirCounts['28'] += 1;  // east of airport -> rwy 28
      else if (norm > 240 && norm < 310)  dirCounts['10'] += 1;  // west of airport -> rwy 10
      else                                dirCounts['other'] += 1;
    }
    let activeRwy = null;
    let activeRwyN = 0;
    for (const k of ['10', '28']) {
      if (dirCounts[k] > activeRwyN) { activeRwy = k; activeRwyN = dirCounts[k]; }
    }
    return { total, landed, inflight, totalPredictions, avgError, activeRwy, activeRwyN };
  }, [aircraftList, airportNow]);

  // Tab strip — rendered above whichever pane is active. Uses .predictions-tab
  // / .predictions-tabs from Predictions.css, NOT the global .filter-btn class
  // which is scoped to specific parent containers and would render invisible.
  const tabStrip = (
    <div className="predictions-tabs">
      <button
        className={`predictions-tab${activeTab === 'arrivals' ? ' active' : ''}`}
        onClick={() => setActiveTab('arrivals')}
      >Arrivals</button>
      <button
        className={`predictions-tab${activeTab === 'departures' ? ' active' : ''}`}
        onClick={() => setActiveTab('departures')}
      >Departures</button>
      <button
        className={`predictions-tab${activeTab === 'ground' ? ' active' : ''}`}
        onClick={() => setActiveTab('ground')}
      >On Ground</button>
      <button
        className={`predictions-tab${activeTab === 'accuracy' ? ' active' : ''}`}
        onClick={() => setActiveTab('accuracy')}
      >Accuracy</button>
    </div>
  );

  if (activeTab === 'departures') {
    return (
      <div className="predictions-screen">
        {tabStrip}
        <PredictionsDepartures
          departureForecastUpdate={departureForecastUpdate}
          liveAircraft={liveAircraft}
          selectedReceiverUuid={selectedReceiverUuid}
          onJumpToHistory={onJumpToHistory}
        />
      </div>
    );
  }

  if (activeTab === 'accuracy') {
    return (
      <div className="predictions-screen">
        {tabStrip}
        <PredictionAccuracy airportIcao={selectedAirport} />
      </div>
    );
  }

  if (activeTab === 'ground') {
    return (
      <div className="predictions-screen">
        {tabStrip}
        <PredictionsOnGround
          selectedAirport={selectedAirport}
          departureForecastUpdate={departureForecastUpdate}
          liveAircraft={liveAircraft}
          selectedReceiverUuid={selectedReceiverUuid}
          onJumpToHistory={onJumpToHistory}
        />
      </div>
    );
  }

  return (
    <div className="predictions-screen">
      {tabStrip}
      <div className="predictions-controls">
        <div className="control-group">
          <label>Airport:</label>
          <select
            value={selectedAirport}
            onChange={(e) => setSelectedAirport(e.target.value)}
            className="airport-select"
          >
            {SUPPORTED_AIRPORTS.map(a => (
              <option key={a.icao} value={a.icao}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label>Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="date-picker"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div className="control-group">
          <label title="NEAR = aircraft currently within 200 km of the destination airport (on approach). LONG-RANGE = aircraft currently > 200 km out (still en route). ALL = no filter.">Range:</label>
          <div className="range-filter-pills">
            <button
              className={`filter-btn${rangeFilter === 'NEAR' ? ' active' : ''}`}
              onClick={() => setRangeFilter('NEAR')}
              title="Aircraft within 200 km of destination — on approach"
            >Near</button>
            <button
              className={`filter-btn${rangeFilter === 'LONG_RANGE' ? ' active' : ''}`}
              onClick={() => setRangeFilter('LONG_RANGE')}
              title="Aircraft &gt; 200 km from destination — still en route"
            >Long-range</button>
            <button
              className={`filter-btn${rangeFilter === 'ALL' ? ' active' : ''}`}
              onClick={() => setRangeFilter('ALL')}
              title="No range filter"
            >All</button>
          </div>
        </div>
        <div className="predictions-filters">
          <span className="filter-label">Filter:</span>
          <button
            className={`filter-btn${filter === 'all' ? ' active' : ''}`}
            onClick={() => setFilter('all')}
          >All</button>
          <button
            className={`filter-btn${filter === 'landed' ? ' active' : ''}`}
            onClick={() => setFilter('landed')}
          >Landed</button>
          <button
            className={`filter-btn${filter === 'inflight' ? ' active' : ''}`}
            onClick={() => setFilter('inflight')}
          >In Flight</button>
          <span className="filter-separator">|</span>
          <span className="filter-label">Airline:</span>
          <select
            className="filter-select"
            value={airlineFilter}
            onChange={e => setAirlineFilter(e.target.value)}
            title="Filter by airline"
          >
            <option value="all">All ({airlineOptions.length})</option>
            {airlineOptions.map(code => (
              <option key={code} value={code}>{airlineLabel(code)}</option>
            ))}
          </select>
          <span className="filter-separator">|</span>
          <span className="airport-clock" title={`Local time at ${selectedAirport}`}>
            🕐 {formatLocalHourMin(airportNow, selectedAirport)} {airportTzName(selectedAirport).split('/')[1] || ''}
          </span>
          {stats.activeRwy && (
            <>
              <span className="filter-separator">|</span>
              <span className="active-runway-badge"
                title={`Inferred from ${stats.activeRwyN} flights landed in the last 60 min`}>
                Active Rwy <strong>{stats.activeRwy}</strong>
              </span>
            </>
          )}
          {arrivalFlow && (
            <>
              <span className="filter-separator">|</span>
              <ArrivalFlowBadge arrivalFlow={arrivalFlow} />
            </>
          )}
          <span className="filter-separator">|</span>
          <button
            className={`filter-btn${toolsOpen ? ' active' : ''}`}
            onClick={() => setToolsOpen(v => !v)}
            title="Map & accuracy tools"
          >⚙ Tools</button>
          {toolsOpen && (
          <div className="predictions-tools-popover">
          <span className="filter-label">Auto Zoom:</span>
          <select
            className="filter-select"
            value={autoZoom}
            onChange={e => setAutoZoom(e.target.value)}
            title="Auto-zoom mode"
          >
            <option value="none">Off</option>
            <option value="airport">Airport (20 km across)</option>
            <option value="local">Local (100 km across)</option>
            <option value="regional">Regional (200 km range)</option>
            <option value="all">All aircraft (fit every inbound)</option>
            <option value="selected">Selected flight + airport</option>
          </select>
          <button
            className={`filter-btn${showAccuracyHeatmap ? ' active' : ''}`}
            onClick={() => setShowAccuracyHeatmap(!showAccuracyHeatmap)}
            title="Show prediction accuracy heatmap"
          >Accuracy Heatmap</button>
          {selectedAirport === 'EIDW' && (
            <select
              value={heatmapModel}
              onChange={e => setHeatmapModel(e.target.value)}
              title="Choose which model's accuracy is shaded on the map"
              style={{ marginLeft: 4 }}
            >
              <option value="v13-conformal">v13-conformal (current)</option>
              <option value="v13">v13 (point)</option>
              <option value="v12-conformal">v12-conformal</option>
              <option value="v12-quantile">v12-quantile</option>
              <option value="v11">v11 (point)</option>
            </select>
          )}
          <button
            className="filter-btn"
            onClick={() => setShowQualityChart(true)}
            title="Show daily prediction-accuracy trend (MedAE, bias, skill score)"
          >Quality Trend</button>
          <button
            className="filter-btn"
            onClick={async () => {
              if (!window.confirm(`Clear accuracy heatmap for ${selectedAirport} / ${selectedAirport === 'EIDW' ? heatmapModel : 'v2'}? Future predictions will build a fresh heatmap (existing history preserved).`)) return;
              try {
                const token = getAccessToken();
                const modelVersion = selectedAirport === 'EIDW' ? heatmapModel : 'v2';
                const resp = await clearPredictionAccuracyHeatmap(selectedAirport, modelVersion, token);
                if (resp.header?.apiStatus === 'OK' || resp.header?.apiStatus === 'SUCCESS') {
                  // Refresh the displayed heatmap
                  setAccuracySectors([]);
                  setMaxMaeInSector(0);
                  setOverallMae(0);
                  if (showAccuracyHeatmap) {
                    // Trigger a re-fetch by toggling off/on
                    setShowAccuracyHeatmap(false);
                    setTimeout(() => setShowAccuracyHeatmap(true), 50);
                  }
                } else {
                  window.alert('Failed to clear heatmap.');
                }
              } catch (e) {
                window.alert(`Error clearing heatmap: ${e.message || e}`);
              }
            }}
            title="Reset accuracy heatmap — future predictions build a fresh view"
          >Clear Heatmap</button>
          </div>
          )}
        </div>
      </div>

      <div className="predictions-content">
        <div className="predictions-map">
          {/* Map framing only. The table is scoped by the range filter above and
              is deliberately unaffected by anything here. */}
          <div className="predictions-zoom-control" role="group" aria-label="Map zoom">
            {ZOOM_PRESETS.map(p => (
              <button
                key={p.id}
                type="button"
                className={`predictions-zoom-btn${autoZoom === p.id ? ' active' : ''}`}
                title={p.title}
                // Re-clicking the active preset re-frames the map, which is what a
                // user who has panned away expects. Setting the same state value
                // would not re-run the effect, so drop to 'none' first.
                onClick={() => {
                  if (autoZoom === p.id) {
                    setAutoZoom('none');
                    setTimeout(() => setAutoZoom(p.id), 0);
                  } else {
                    setAutoZoom(p.id);
                  }
                }}
              >{p.label}</button>
            ))}
          </div>
          <MapContainer
            key={selectedAirport}
            center={[airport.lat, airport.lon]}
            zoom={8}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url={tileConfig.url} attribution={tileConfig.attribution} />
            <PredictionsTileFilter filter={mapFilter} />

            {/* Accuracy heatmap sectors */}
            {accuracyPolygons.map(s => (
              <Polygon
                key={s.key}
                positions={s.positions}
                pathOptions={{
                  color: 'rgba(255,255,255,0.4)',
                  fillColor: s.color,
                  fillOpacity: s.fillOpacity,
                  weight: 1,
                  opacity: 0.8
                }}
              >
                <Tooltip>
                  <div>
                    <strong>Bearing:</strong> {s.bearingStart}° - {s.bearingEnd}°<br />
                    <strong>Distance:</strong> {convertDistance(s.distanceStart, unitSystem).toFixed(0)} - {convertDistance(s.distanceEnd, unitSystem).toFixed(0)} {units.distance}<br />
                    <strong>Predictions:</strong> {s.predictionCount?.toLocaleString() || 0}<br />
                    <strong>Avg ETA:</strong> {s.avgEtaMinutes?.toFixed(1) || '-'} min<br />
                    <strong>ETA Range:</strong> {s.minEtaMinutes?.toFixed(1) || '-'} - {s.maxEtaMinutes?.toFixed(1) || '-'} min<br />
                    {s.hasLandingData && (<>
                      <strong>MAE:</strong> {s.meanAbsoluteError?.toFixed(2) || '-'} min<br />
                      <strong>Max Error:</strong> {s.maxError?.toFixed(1) || '-'} min
                    </>)}
                  </div>
                </Tooltip>
              </Polygon>
            ))}

            {/* Selected aircraft track from prediction events.
                Rendered as multiple segments so gaps (e.g., restart outages)
                don't draw as straight-line interpolations. */}
            {trackSegments.map((seg, i) => (
              <Polyline
                key={`seg-${i}`}
                positions={seg}
                pathOptions={{ color: '#3b82f6', weight: 3, opacity: 0.9 }}
              />
            ))}

            {/* Prediction event markers along the track.
                ESTIMATE rows → small dot coloured by ETA change.
                LANDED row    → large green terminal marker. */}
            {predictionEvents.map((event, idx) => {
              if (event.latitude == null || event.longitude == null) return null;

              if (event.eventType === 'LANDED') {
                return (
                  <CircleMarker
                    key={`pe-landed-${event.id || idx}`}
                    center={[event.latitude, event.longitude]}
                    radius={9}
                    pathOptions={{
                      color: cssVar('--text-primary') || '#ffffff',
                      fillColor: cssVar('--status-connected') || '#22c55e',
                      fillOpacity: 1,
                      weight: 2.5
                    }}
                  >
                    <Tooltip permanent direction="top" offset={[0, -8]}>
                      <div style={{ fontWeight: 700, color: 'var(--status-connected-text, #166534)' }}>
                        LANDED {formatServerTime(event.predictionAt, selectedAirport)}
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              }

              // Compare with previous ESTIMATE's ETA to colour the dot
              const prev = idx > 0 ? predictionEvents[idx - 1] : null;
              const change = prev && prev.eventType !== 'LANDED' ? event.etaMinutes - prev.etaMinutes : 0;
              const color = errorToColor(change);
              return (
                <CircleMarker
                  key={`pe-${event.id || idx}`}
                  center={[event.latitude, event.longitude]}
                  radius={5}
                  pathOptions={{
                    color: '#ffffff',
                    fillColor: color,
                    fillOpacity: 0.95,
                    weight: 1.5
                  }}
                >
                  <Tooltip>
                    <div>
                      <strong>{formatServerTime(event.predictionAt, selectedAirport)}</strong><br />
                      ETA: {formatMinutes(event.etaMinutes)}<br />
                      ETA Time: {formatEtaTime(event.etaTime, selectedAirport)}<br />
                      Distance: {event.distanceKm != null ? convertDistance(event.distanceKm, unitSystem).toFixed(1) : '-'} {units.distance}<br />
                      Altitude: {event.altitudeFeet != null ? `${Math.round(convertAltitude(event.altitudeFeet, unitSystem)).toLocaleString()} ${units.altitude}` : '-'}
                      {prev && prev.eventType !== 'LANDED' && <><br />Δ vs prev: {formatDelta(change)}</>}
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}

            {/* All live inbound aircraft — real AircraftMarker (matches Live page).
                Expanded flight passes isSelected=true so it renders in selected
                (orange) state, consistent with Live page selection. Clicking a
                marker expands/collapses its row in the table. */}
            {liveInboundMarkers.map(m => (
              <AircraftMarker
                key={`live-${m._predictionKey}`}
                aircraft={m}
                isSelected={m._isExpanded}
                zoomLevel={8}
                onAircraftSelect={(clicked) => {
                  // Re-use the table's expand handler so selection state stays in sync.
                  const [icao, cs, fpa] = (m._predictionKey || '').split('|');
                  if (icao) handleExpandFlight(icao, cs, fpa);
                }}
              />
            ))}

            {/* Track of the selected (expanded) flight. */}
            <LandingTrackLine positions={landingTrack.positions} />

            <PredictionsAutoZoom
              mode={autoZoom}
              markers={liveInboundMarkers}
              airport={airport}
              expandedKey={expandedKey}
            />

            {/* Airport marker */}
            <Marker
              position={[airport.lat, airport.lon]}
              icon={L.divIcon({
                html: `<div class="prediction-airport-marker">${airport.icao}</div>`,
                className: 'prediction-airport-icon',
                iconSize: [50, 22],
                iconAnchor: [25, 11]
              })}
            />
          </MapContainer>

          <LandingTrackPopup hex={expandedFlight?.icaoAddress} track={landingTrack} liveAircraft={liveAircraft} />

          {/* Stats overlay */}
          <div className="prediction-stats-overlay">
            <div className="stat-item">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Aircraft</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.landed}</div>
              <div className="stat-label">Landed</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.inflight}</div>
              <div className="stat-label">In Flight</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.totalPredictions.toLocaleString()}</div>
              <div className="stat-label">Predictions</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.avgError.toFixed(1)}m</div>
              <div className="stat-label">Avg Error</div>
            </div>
            {showAccuracyHeatmap && (
              <div className="stat-item">
                <div className="stat-value">{overallMae.toFixed(1)}m</div>
                <div className="stat-label">Sector MAE</div>
              </div>
            )}
          </div>
        </div>

        {/* Resize divider */}
        <div
          className="resize-divider"
          onMouseDown={handleResizeMouseDown}
          onTouchStart={handleResizeTouchStart}
          style={{
            width: '8px',
            cursor: 'ew-resize',
            backgroundColor: isResizing ? '#FF6B35' : '#4a5568',
            flexShrink: 0,
            transition: isResizing ? 'none' : 'background-color 0.2s',
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderLeft: '1px solid #2d3748',
            borderRight: '1px solid #2d3748',
            touchAction: 'none'
          }}
          onMouseEnter={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#FF6B35';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#4a5568';
          }}
        >
          <div style={{
            width: '2px',
            height: '40px',
            background: 'repeating-linear-gradient(0deg, #718096 0px, #718096 4px, transparent 4px, transparent 8px)',
            pointerEvents: 'none'
          }} />
        </div>

        <div className="predictions-table-container" style={{ width: `${tableWidth}%` }}>
          {loading ? (
            <div className="loading-message">Loading predictions...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : sortedAircraft.length === 0 ? (
            <div className="no-data">No predictions for this date</div>
          ) : (
            <table className="predictions-table">
              <thead>
                {/* Two-line blocks: each column header names the line it sorts. Line 1 is the
                    headline value, line 2 its detail. Keep the column order in step with the
                    tablet nth-child rules in Predictions.css. */}
                <tr>
                  <th></th>
                  <th className="seq-col" title="Predicted landing order (1 = lands next), ranked by ETA">#</th>
                  <SortTh sort={sortProps} k1="callsign" l1="Flight" k2="registration" l2="reg"
                    t1="Callsign (as on the map) and published flight number" t2="Registration" />
                  <SortTh sort={sortProps} k1="aircraftType" l1="Aircraft" k2="icaoAddress" l2="hex"
                    t1="ICAO type (H = heavy, M = military)" t2="Mode-S address — click it to open History" />
                  <SortTh sort={sortProps} k1="originIata" l1="Route" k2="gate" l2="gate"
                    t1="Origin → destination (✓ = departed origin)" t2="Assigned gate · terminal" />
                  <SortTh sort={sortProps} numeric k1="scheduledArrival" l1="Sched" k2="airlineEstimatedArrival" l2="airline"
                    t1="Airline-published scheduled arrival" t2="Airline's estimated arrival (airlabs, refreshed every 30 min)" />
                  <SortTh sort={sortProps} numeric className="eta-time-col" k1="lastEtaTime" l1="Our ETA" l2="outlook"
                    t1="Our model's predicted landing time" t2="Early / on time / late vs schedule, with trend" />
                  <SortTh sort={sortProps} numeric k1="lastEtaMinutes" l1="To go" k2="currentDistanceKm" l2={`${units.distance} / max`}
                    t1="Time to landing (mm ss)" t2="Distance now / furthest detection range" />
                  <SortTh sort={sortProps} k1="_holdRank" l1="Hold" k2="holdMinutes" l2="time"
                    t1="Holding now, held on this approach, or likely to hold before landing"
                    t2="Time in the hold, its duration, or the expected hold" />
                  <SortTh sort={sortProps} numeric k1="firstDetectEtaTime" l1="1st det" l2="Δ / km"
                    t1="ETA at first detection. After landing: error vs actual (+ = predicted late)"
                    t2="Range at first detection; after landing, the predicted time" />
                  <SortTh sort={sortProps} numeric k1="checkpoint100EtaTime" l1="100 km" l2="Δ / km"
                    t1="ETA when first inside 100 km. After landing: error vs actual" />
                  <SortTh sort={sortProps} numeric k1="checkpoint10EtaTime" l1="10 km" l2="Δ / km"
                    t1="ETA when first inside 10 km. After landing: error vs actual" />
                  <SortTh sort={sortProps} k1="actualLandedAt" l1="Status" k2="predictionCount" l2="landed / #"
                    t1="Live, landed, or contact lost" t2="Landing time, or the number of predictions made" />
                </tr>
              </thead>
              <tbody>
                {sortedAircraft.map((a, idx) => {
                  // Include firstPredictionAt in the key so that an aircraft flying
                  // multiple sessions under the same callsign today (each returned
                  // as a separate summary row by the backend since 1.0.115) gets
                  // distinct React keys and distinct expand state.
                  const rowKey = `${a.icaoAddress}|${a.callsign || ''}|${a.firstPredictionAt || ''}`;
                  const isExpanded = expandedKey === rowKey;
                  // Derived on the row in sortedAircraft dedup (see above).
                  const presumedLanded = !!a._presumedLanded;
                  // Current range, freshest source first: the live WS event, then the backend's
                  // lastDistanceKm (range at the most recent prediction), then minDistanceKm.
                  // minDistanceKm is the session MINIMUM and only equals the current range while
                  // the flight is closing — after a hold or a go-around it understates it, which
                  // is why it is last. Suppressed once the flight is down, where "0.3 km from the
                  // airport" is noise, not news.
                  const currentDistKm = a.currentDistanceKm != null ? a.currentDistanceKm
                    : (a.lastDistanceKm != null ? a.lastDistanceKm : a.minDistanceKm);
                  // In-Flight / Landed group dividers. sortedAircraft already
                  // groups in-flight first, then landed, when sorted by ETA.
                  const isLanded = !!a.actualLandedAt || a._presumedLanded;
                  const prevA = idx > 0 ? sortedAircraft[idx - 1] : null;
                  const prevLanded = prevA ? (!!prevA.actualLandedAt || prevA._presumedLanded) : false;
                  const showInFlightHeader = idx === 0 && !isLanded;
                  const showLandedHeader = isLanded && (idx === 0 || !prevLanded);
                  const rowVisible = !isLanded || !landedCollapsed;
                  // Inbound emergency: the backend's episode for this approach, or the live
                  // squawk so a new one shows before the next refresh. Emergencies take landing
                  // priority, which is why they sit beside the hold indicator's story.
                  const liveSquawk = isLanded ? null : getEmergencySquawk(liveFor(a));
                  const emergency = getEmergencySquawk(a.emergencySquawk) || liveSquawk;
                  const emergencyActive = !!liveSquawk || (!!a.emergencySquawk && !a.emergencyEndedAt && !isLanded);
                  return (
                    <React.Fragment key={rowKey}>
                      {showInFlightHeader && (
                        <tr className="prediction-group-divider">
                          <td colSpan={100}><span className="grp-icon">✈</span> In Flight <span className="grp-count">{inFlightTotal}</span></td>
                        </tr>
                      )}
                      {showLandedHeader && (
                        <tr className="prediction-group-divider landed"
                            onClick={(e) => { e.stopPropagation(); setLandedCollapsed(v => !v); }}
                            title={landedCollapsed ? 'Show landed flights' : 'Hide landed flights'}>
                          <td colSpan={100}>
                            <span className="grp-caret">{landedCollapsed ? '▸' : '▾'}</span>{' '}
                            <span className="grp-icon">✓</span> Landed <span className="grp-count">{landedTotal}</span>
                          </td>
                        </tr>
                      )}
                      {rowVisible && (<>
                      <tr
                        className={`${isExpanded ? 'expanded' : ''}${a._stale && !isLanded ? ' stale-contact' : ''}${emergencyActive ? ' row-emergency' : (a.divertedAt ? ' row-diverted' : (a.holdState === 'HOLDING' && !isLanded ? ' row-holding' : ''))}`.trim()}
                        onClick={() => handleExpandFlight(a.icaoAddress, a.callsign, a.firstPredictionAt)}
                      >
                        <td className="expand-cell" data-label-none>{isExpanded ? '▼' : '▶'}</td>
                        <td className="seq-cell" data-label="Seq">
                          {isLanded
                            ? <span className="seq-landed" title="Landed">✓</span>
                            : (arrivalOrderByKey.has(rowKey)
                              ? <span className="seq-num">{arrivalOrderByKey.get(rowKey)}</span>
                              : null)}
                        </td>
                        {/* FLIGHT — callsign (headline) / published flight number · registration */}
                        <td data-label="Flight">
                          <div className="pt-stack">
                            <div className="pt-l1">
                              <span className="pt-inline">
                                <AirlineLogo airlineCode={a.airlineIcao} airlineName={a.airlineIcao} />
                                <span className="pt-key">{a.callsign || '-'}</span>
                                {a.iataFlightNumber && a.iataFlightNumber !== a.callsign && (
                                  <span className="pt-muted" title="Published flight number">{a.iataFlightNumber}</span>
                                )}
                                {emergency && (
                                  <span className={`pt-emergency${emergencyActive ? '' : ' ended'}`}
                                    title={`Squawked ${emergency.code} — ${emergency.description}`
                                      + (a.emergencyAt ? ` from ${formatLocalHourMin(a.emergencyAt, selectedAirport)}` : '')
                                      + (a.emergencyEndedAt ? `, back to normal ${formatLocalHourMin(a.emergencyEndedAt, selectedAirport)}` : '')
                                      + '. Emergencies take landing priority.'}>
                                    {emergency.code} {emergency.label}
                                  </span>
                                )}
                                <ApproachFlags row={a} fmt={(t) => formatLocalHourMin(t, selectedAirport)} />
                              </span>
                            </div>
                            <div className="pt-l2" title={a.aircraftDescription || ''}>
                              {a.registration || ' '}
                            </div>
                          </div>
                        </td>
                        {/* AIRCRAFT — type with heavy / military marks / flag + hex (History link) */}
                        <td data-label="Aircraft">
                          <div className="pt-stack">
                            <div className="pt-l1">
                              <span className="pt-inline">
                                {a.aircraftType || '-'}
                                {a.aircraftType && <AircraftTypeIcon aircraftType={a.aircraftType} size={14} />}
                                {isHeavyType(a.aircraftType) && (
                                  <span className="heavy-badge" title="ICAO Wake Turbulence Cat H — Heavy">H</span>
                                )}
                                {a.military && (
                                  <span className="heavy-badge military-badge" title="Military aircraft">M</span>
                                )}
                              </span>
                            </div>
                            <div className="pt-l2">
                              <span className="pt-inline" title={a.country || ''}>
                                <FlagIcon countryCode={resolveCountryCode(a, { hex: 'icaoAddress' })} country={a.country} />
                                {a.icaoAddress ? (
                                  <a
                                    href="#"
                                    className="pt-link"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (typeof onJumpToHistory === 'function') onJumpToHistory(a.icaoAddress);
                                    }}
                                    title={`View ${a.icaoAddress} in History${a.ownerOperator ? ' — ' + a.ownerOperator : ''}`}
                                  >
                                    {a.icaoAddress}
                                  </a>
                                ) : '-'}
                              </span>
                            </div>
                          </div>
                        </td>
                        {/* ROUTE — origin → destination / gate · terminal */}
                        <td data-label="Route" title={(() => {
                          const parts = [];
                          if (a.originName) parts.push(a.originName);
                          if (a.originActualDeparture) {
                            parts.push(`Departed origin: ${formatLocalHourMin(a.originActualDeparture, selectedAirport)}`);
                          } else if (a.originEstimatedDeparture) {
                            parts.push(`Estimated dep from origin: ${formatLocalHourMin(a.originEstimatedDeparture, selectedAirport)}`);
                          } else if (a.originScheduledDeparture) {
                            parts.push(`Scheduled dep from origin: ${formatLocalHourMin(a.originScheduledDeparture, selectedAirport)}`);
                          }
                          if (a.terminal) parts.push(`Terminal ${a.terminal}`);
                          return parts.join(' — ');
                        })()}>
                          <div className="pt-stack">
                            <div className="pt-l1">
                              <span className="pt-key">{a.originIata || '—'}</span>
                              {a.originActualDeparture && <span className="status-check pt-gap" title="Departed origin">✓</span>}
                              <span className="pt-muted"> → {destIata}</span>
                            </div>
                            <div className="pt-l2">
                              {a.gate ? `Gate ${a.gate}${a.terminal ? ` · T${a.terminal}` : ''}` : ' '}
                            </div>
                          </div>
                        </td>
                        {/* SCHED — airline schedule / airline estimate */}
                        <td className="numeric" data-label="Sched">
                          <div className="pt-stack">
                            <div className="pt-l1" title={a.scheduledArrival ? 'Scheduled arrival' : 'No schedule data'}>
                              {a.scheduledArrival ? formatLocalHourMin(a.scheduledArrival, selectedAirport) : '—'}
                            </div>
                            <div className="pt-l2" title={a.airlineEstimatedArrival ? 'Airline-reported ETA' : 'No airline ETA'}>
                              {a.airlineEstimatedArrival ? formatLocalHourMin(a.airlineEstimatedArrival, selectedAirport) : ' '}
                            </div>
                          </div>
                        </td>
                        {/* OUR ETA — the headline number / outlook bar (final error once landed) */}
                        <td className="numeric eta-time-cell" data-label="Our ETA">
                          <div className="pt-stack">
                            <div className="pt-l1">
                              {a.actualLandedAt ? (
                                <>
                                  <span className="status-check" title="Actual landing">✓</span>{' '}
                                  <span className="eta-time-value">{formatLocalHourMin(a.actualLandedAt, selectedAirport)}</span>
                                </>
                              ) : presumedLanded ? (
                                <>
                                  <span className="status-check" style={{ opacity: 0.7 }} title="Predicted landing time (LANDED event not detected)">~</span>{' '}
                                  <span className="eta-time-value" style={{ opacity: 0.7 }}>{formatLocalHourMin(a.lastEtaTime, selectedAirport)}</span>
                                </>
                              ) : (
                                <>
                                  <span className="eta-time-value">{formatLocalHourMin(a.lastEtaTime, selectedAirport)}</span>
                                  {(() => {
                                    // Arrival-sequencer indicator: this ETA was pushed out to
                                    // keep landing separation from traffic ahead. Show a ⏱ badge
                                    // with the raw model ETA + push amount (estimate validation).
                                    const lv = liveFor(a);
                                    if (!lv || !lv.eta_sequence_adjusted) return null;
                                    const push = (lv.eta_raw_minutes != null && lv.eta_minutes != null)
                                      ? Math.max(0, lv.eta_minutes - lv.eta_raw_minutes) : null;
                                    const pushTxt = push != null ? `+${Math.round(push)} min` : '';
                                    return (
                                      <span
                                        className="eta-seq-badge"
                                        title={`Queue-adjusted ${pushTxt} for landing separation${lv.eta_raw_minutes != null ? ` (raw model ${Math.round(lv.eta_raw_minutes)} min out)` : ''}`}
                                        style={{ marginLeft: 4, opacity: 0.85, cursor: 'help' }}
                                      >⏱{push != null && push >= 1 ? `+${Math.round(push)}` : ''}</span>
                                    );
                                  })()}
                                  {(() => {
                                    // Uncertainty chip, for flights with NO schedule to be punctual
                                    // against — GA and unscheduled traffic. A p10–p90 width, which is
                                    // a different statement from the outlook bar below.
                                    //
                                    // There used to be a second chip here for scheduled flights:
                                    // P(arrival > scheduled + 15m). It has been removed because it
                                    // asked the outlook bar's question with a different tolerance
                                    // (+15 against the bar's ±10), so one row published "91% delay"
                                    // beside "95% late" — one measurement quoted twice, differing
                                    // only by where "late" started, and reading like two independent
                                    // measurements agreeing. The bar now carries the single number,
                                    // at the single tolerance in ON_TIME_TOLERANCE_MIN.
                                    if (a.scheduledArrival
                                        && parseDistribution(a.distributionJson)
                                        && (a.distributionPredictionAt || a.lastPredictionAt)) {
                                      return null;
                                    }
                                    const spread = (a.etaP90Minutes != null && a.etaP10Minutes != null)
                                      ? (a.etaP90Minutes - a.etaP10Minutes) : null;
                                    if (spread == null) return null;
                                    if (spread > 20) {
                                      return <span className="prediction-risk-chip risk-high" title={`Wide uncertainty band (p10–p90 spans ${spread.toFixed(0)} min)`}>±{spread.toFixed(0)}min</span>;
                                    }
                                    if (spread > 10) {
                                      return <span className="prediction-risk-chip risk-medium" title={`Moderate uncertainty (p10–p90 spans ${spread.toFixed(0)} min)`}>±{spread.toFixed(0)}min</span>;
                                    }
                                    return null;
                                  })()}
                                </>
                              )}
                            </div>
                            <div className="pt-l2">
                              {!a.actualLandedAt ? (() => {
                                // Early / on-time / late vs scheduled time (taxi-adjusted),
                                // with the live delay-risk trend arrow.
                                const o = arrivalOutlook({
                                  distributionJson: a.distributionJson,
                                  predictionAt: a.distributionPredictionAt || a.lastPredictionAt,
                                  scheduledArrival: a.scheduledArrival,
                                  airlineEstimatedArrival: a.airlineEstimatedArrival,
                                });
                                if (!o) return ' ';
                                return <ArrivalOutlookBar outlook={o} trendDelta={lateTrendDelta(a)} compact />;
                              })() : (a.finalErrorMinutes != null ? (
                                // finalErrorMinutes = actual - predicted, so negate for the
                                // checkpoint convention (+ = predicted late).
                                <span className={`pt-err ${errorToBandClass(a.finalErrorMinutes)}`}
                                  title="Last prediction vs actual landing (+ = predicted late)">
                                  {formatDelta(-a.finalErrorMinutes)} final
                                </span>
                              ) : ' ')}
                            </div>
                          </div>
                        </td>
                        {/* TO GO — countdown / distance now · max range */}
                        <td className="numeric" data-label="To go">
                          <div className="pt-stack">
                            <div className="pt-l1 pt-key">
                              {(a.actualLandedAt || presumedLanded)
                                ? '—'
                                : a._stale
                                  ? <span className="remaining-stale" title="Counting down from a prediction we no longer trust">—</span>
                                  : (() => {
                                    if (a.lastEtaTime) {
                                      const remainingMin = (parseServerTime(a.lastEtaTime).getTime() - airportNow.getTime()) / 60000;
                                      return formatRemainingMmSs(remainingMin);
                                    }
                                    return formatRemainingMmSs(a.lastEtaMinutes);
                                  })()}
                            </div>
                            <div className="pt-l2" title={`Distance now / furthest detection range (${units.distance})`}>
                              {isLanded ? '—' : (
                                `${currentDistKm == null ? '—' : convertDistance(currentDistKm, unitSystem).toFixed(0)}`
                                + ` / ${a.maxDistanceKm != null ? convertDistance(a.maxDistanceKm, unitSystem).toFixed(0) : '—'}`
                              )}
                            </div>
                          </div>
                        </td>
                        {/* HOLD — holding / held / likely, with time */}
                        <td data-label="Hold">
                          <div className="pt-stack">
                            <HoldCellContent row={a} nowMs={airportNow.getTime()} />
                          </div>
                        </td>
                        {/* Checkpoints. In flight: the ETA predicted there / the range it was made at.
                            Landed: the signed error vs the actual landing (+ = predicted late) / the
                            time that was predicted. */}
                        {renderCheckpoint(a, a.firstDetectEtaTime, a.firstDetectDistanceKm, 'First detect')}
                        {renderCheckpoint(a, a.checkpoint100EtaTime, a.checkpoint100DistanceKm, '100 km')}
                        {renderCheckpoint(a, a.checkpoint10EtaTime, a.checkpoint10DistanceKm, '10 km')}
                        {/* STATUS — live / landed / lost contact, then landing time or prediction count */}
                        <td className="status-cell" data-label="Status">
                          <div className="pt-stack">
                            <div className="pt-l1">
                              {(() => {
                                if (a.actualLandedAt) {
                                  return (
                                    <span className="status-landed" title={`Landed at ${formatLocalHourMin(a.actualLandedAt, selectedAirport)}`}>
                                      <span className="status-check">✓</span> Landed
                                    </span>
                                  );
                                }
                                if (presumedLanded) {
                                  return (
                                    <span className="status-landed" style={{ opacity: 0.7 }}
                                      title={`Predicted ETA ${formatLocalHourMin(a.lastEtaTime, selectedAirport)} was over 5 min ago — LANDED event not detected`}>
                                      <span className="status-check">~</span> Landed (est)
                                    </span>
                                  );
                                }
                                if (a._stale) {
                                  const mins = Number.isFinite(a._predAgeMin) ? Math.round(a._predAgeMin) : null;
                                  return (
                                    <span className="status-stale"
                                      title={`No predictions for ${mins != null ? mins + ' min' : 'a long time'}`
                                        + ` — last contact at ${formatLocalHourMin(a.lastPredictionAt, selectedAirport)}.`
                                        + ' We do not know whether this flight has landed.'}>
                                      ⚠ No contact{mins != null ? ` ${mins}m` : ''}
                                    </span>
                                  );
                                }
                                // A flight is LIVE if we're actively producing predictions for it.
                                // Prediction cadence is 20s so anything within 120s is effectively live.
                                // Independent of the WS liveAircraft prop — works for any airport.
                                const lastPredMs = a.lastPredictionAt ? parseServerTime(a.lastPredictionAt).getTime() : 0;
                                const isLive = (airportNow.getTime() - lastPredMs) < 120 * 1000;
                                if (isLive) {
                                  return (
                                    <span className="status-live" title="Actively receiving predictions">
                                      <span className="status-dot" /> LIVE
                                    </span>
                                  );
                                }
                                return <span className="status-unknown" title="No recent predictions">—</span>;
                              })()}
                            </div>
                            <div className="pt-l2" title={`${a.predictionCount || 0} predictions`}>
                              {a.actualLandedAt
                                ? formatLocalHourMin(a.actualLandedAt, selectedAirport)
                                : `#${a.predictionCount || 0}`}
                            </div>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="prediction-events-row">
                          <td colSpan="100">
                            {eventsLoading ? (
                              <div className="loading-message">Loading prediction events...</div>
                            ) : predictionEvents.length === 0 ? (
                              <div className="no-data">No prediction events</div>
                            ) : (
                              <>
                                {a.actualLandedAt && (
                                  <div className="actual-landing-banner">
                                    <span className="label">Actual landing:</span>
                                    <span className="value">{formatServerTime(a.actualLandedAt, selectedAirport)}</span>
                                  </div>
                                )}
                                {!a.actualLandedAt && (() => {
                                  // Arrival outlook summary + delay-risk trend over this
                                  // flight's prediction cycles.
                                  const o = arrivalOutlook({
                                    distributionJson: a.distributionJson,
                                    predictionAt: a.distributionPredictionAt || a.lastPredictionAt,
                                    scheduledArrival: a.scheduledArrival,
                                    airlineEstimatedArrival: a.airlineEstimatedArrival,
                                  });
                                  if (!o) return null;
                                  const pts = predictionEvents
                                    .filter(ev => ev.distributionJson)
                                    .map(ev => {
                                      const oo = arrivalOutlook({
                                        distributionJson: ev.distributionJson,
                                        predictionAt: ev.predictionAt,
                                        scheduledArrival: a.scheduledArrival,
                                        airlineEstimatedArrival: a.airlineEstimatedArrival,
                                      });
                                      const t = parseServerTime(ev.predictionAt)?.getTime();
                                      return oo && t != null ? { t, early: oo.early, within: oo.within, late: oo.late } : null;
                                    })
                                    .filter(Boolean)
                                    .sort((x, y) => x.t - y.t);
                                  // Per-item trend rate: change in each probability over the
                                  // last ~10 min — drives the rotating legend arrows.
                                  let trendRates = null;
                                  if (pts.length >= 2) {
                                    const last = pts[pts.length - 1];
                                    const refT = last.t - 10 * 60000;
                                    let ref = pts[0];
                                    for (const s of pts) { if (s.t <= refT) ref = s; }
                                    trendRates = { early: last.early - ref.early, within: last.within - ref.within, late: last.late - ref.late };
                                  }
                                  return (
                                    <div className="ao-summary">
                                      <ArrivalOutlookBar outlook={o} trendRates={trendRates} />
                                      {pts.length >= 2 && (
                                        <div className="ao-summary-trend">
                                          <span className="ao-summary-trend-label">Delay-risk trend</span>
                                          <LateTrendSparkline points={pts} />
                                          <span className="ao-summary-trend-val">
                                            {Math.round(pts[pts.length - 1].late * 100)}% late · was {Math.round(pts[0].late * 100)}% {Math.round((pts[pts.length - 1].t - pts[0].t) / 60000)} min ago
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                <table className="prediction-events-table">
                                  <thead>
                                    <tr>
                                      <th>Time</th>
                                      <th title="Model version that produced this row">Model</th>
                                      <th className="numeric">Distance ({units.distance})</th>
                                      <th className="numeric">Bearing</th>
                                      <th className="numeric">Altitude ({units.altitude})</th>
                                      <th className="numeric">ETA (min)</th>
                                      <th className="numeric">ETA Time</th>
                                      <th className="numeric" title="p10–p50–p90 quantile spread (v12 models only)">Quantiles</th>
                                      <th>Distribution</th>
                                      <th className="numeric">Δ vs prev</th>
                                      <th className="numeric" title="Error = predicted ETA time − actual landing">Error</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {eventsForTable.map((ev, idx) => {
                                      // LANDED terminal row — actual touchdown,
                                      // rendered as a distinct banner row across
                                      // the whole width.
                                      if (ev.eventType === 'LANDED') {
                                        return (
                                          <tr key={ev.id || `landed-${idx}`} className="prediction-event-landed">
                                            <td>{formatServerTime(ev.predictionAt, selectedAirport)}</td>
                                            <td colSpan="10" className="landed-cell">
                                              <span className="landed-badge">LANDED</span>
                                              <span className="landed-text">Actual touchdown</span>
                                            </td>
                                          </tr>
                                        );
                                      }

                                      // eventsForTable is sorted DESC. For delta calc,
                                      // find the next chronologically-earlier ESTIMATE
                                      // from the SAME model version — comparing v12 to
                                      // v11 produces meaningless deltas. Skip LANDED
                                      // rows that might sit between.
                                      let prev = null;
                                      for (let j = idx + 1; j < eventsForTable.length; j++) {
                                        const cand = eventsForTable[j];
                                        if (cand.eventType === 'LANDED') continue;
                                        if ((cand.modelVersion || '') === (ev.modelVersion || '')) {
                                          prev = cand;
                                          break;
                                        }
                                      }
                                      const delta = prev ? ev.etaMinutes - prev.etaMinutes : null;
                                      const deltaColor = delta != null ? errorToColor(delta) : null;
                                      const evDistParsed = parseDistribution(ev.distributionJson);
                                      const quantileText = (ev.etaP10Minutes != null && ev.etaP50Minutes != null && ev.etaP90Minutes != null)
                                        ? `${ev.etaP10Minutes.toFixed(1)}–${ev.etaP50Minutes.toFixed(1)}–${ev.etaP90Minutes.toFixed(1)}`
                                        : '—';

                                      // Per-event error against actual landing time, if the
                                      // flight has already landed. Positive = predicted late,
                                      // negative = predicted early.
                                      let errorMin = null;
                                      if (a.actualLandedAt && ev.etaTime) {
                                        errorMin = (parseServerTime(ev.etaTime).getTime()
                                          - parseServerTime(a.actualLandedAt).getTime()) / 60000;
                                      }
                                      const errorColor = errorMin != null ? errorToColor(errorMin) : null;

                                      return (
                                        <tr key={ev.id || idx}>
                                          <td>{formatServerTime(ev.predictionAt, selectedAirport)}</td>
                                          <td><span className="model-version-tag" title="Emitting model version">{ev.modelVersion || '—'}</span></td>
                                          <td className="numeric">{ev.distanceKm != null ? convertDistance(ev.distanceKm, unitSystem).toFixed(1) : '-'}</td>
                                          <td className="numeric">{ev.bearingFromAirport?.toFixed(0) || '-'}°</td>
                                          <td className="numeric">{ev.altitudeFeet != null ? Math.round(convertAltitude(ev.altitudeFeet, unitSystem)).toLocaleString() : '-'}</td>
                                          <td className="numeric">{ev.etaMinutes.toFixed(1)}</td>
                                          <td className="numeric">{formatEtaTime(ev.etaTime, selectedAirport)}</td>
                                          <td className="numeric" title="p10 – p50 – p90 (minutes-to-land)">{quantileText}</td>
                                          <td style={{ minWidth: 140 }}>
                                            {evDistParsed ? (
                                              <PredictionDistributionBar
                                                distribution={evDistParsed}
                                                predictionAt={ev.predictionAt}
                                                airportTz={airportTzName(selectedAirport)}
                                              />
                                            ) : '—'}
                                          </td>
                                          <td
                                            className="numeric"
                                            style={deltaColor ? { color: deltaColor, fontWeight: 600 } : {}}
                                          >
                                            {delta != null ? formatDelta(delta) : '-'}
                                          </td>
                                          <td
                                            className="numeric"
                                            style={errorColor ? { color: errorColor, fontWeight: 600 } : {}}
                                          >
                                            {errorMin != null ? formatDelta(errorMin) : '-'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                      </>)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showQualityChart && (
        <PredictionQualityChart
          airportIcao={selectedAirport}
          onClose={() => setShowQualityChart(false)}
        />
      )}
    </div>
  );
}

export default React.memo(Predictions);
